/**
 * Gift Auto-Add Extension
 * 
 * A completely standalone gift management system that automatically adds gifts to cart
 * when trigger products are added. Simply add data-gift-trigger="product-handle" to any
 * add-to-cart button and this extension will handle the rest.
 * 
 * Usage:
 * 1. Import this file into your project
 * 2. Add data-gift-trigger="product-handle" to your add-to-cart buttons
 * 3. Configure gift rules in window.GIFT_RULES
 * 
 * Example:
 * <button data-gift-trigger="the-complete-system" onclick="addToCart()">
 *   Add to Cart
 * </button>
 */

(function() {
  'use strict';

  const CONFIG = {
    DEBUG: false,
    GIFT_PROPERTY: '_autoGift',
    CART_ENDPOINTS: {
      ADD: '/cart/add.js',
      UPDATE: '/cart/update.js',
      GET: '/cart.js'
    },
    EVENTS: {
      CART_UPDATED: 'gift:cartUpdated',
      GIFT_ADDED: 'gift:giftAdded',
      GIFT_REMOVED: 'gift:giftRemoved'
    },
    DELAYS: {
      CART_PROCESSING: 100,
      CART_UPDATE: 150,
      FORM_SUBMIT: 600,
      POST_CART_ADD: 500,
      POST_CART_ADD_COOLDOWN: 2000
    },
    TIMEOUTS: {
      GIFT_RULES_CHECK: 10000,
      GIFT_RULES_WATCH: 30000,
      ATC_BUTTON_OBSERVER: 30000
    }
  };

  const JSON_HEADERS = { 
    'Content-Type': 'application/json', 
    'Accept': 'application/json' 
  };

  const isGiftItem = (item) => {
    const v = item?.properties?.[CONFIG.GIFT_PROPERTY];
    if (v === true || v === 'true' || v === 'True') return true;
    if (typeof v === 'string' && v.toLowerCase() === 'true') return true;
    return false;
  };
  const isDiscountCodeGiftItem = (item) => {
    const v = item?.properties?._discountCodeGift;
    if (v === true || v === 'true' || v === 'True') return true;
    if (typeof v === 'string' && v.toLowerCase() === 'true') return true;
    return false;
  };
  const isAnyGiftItem = (item) => isGiftItem(item) || isDiscountCodeGiftItem(item);

  /**
   * The most recent cart this extension read. Click handlers have to decide
   * whether to take over a removal before the theme's own listener runs, and that
   * is one synchronous tick — no room to fetch. Every decision made from here is
   * re-checked against a fresh cart before anything is written.
   */
  let lastKnownCart = null;

  const normalizePurchaseType = (type) => String(type || '').trim();
  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  /** @param {unknown} gifts */
  function normalizeGiftHandles(gifts) {
    if (Array.isArray(gifts)) return gifts;
    if (typeof gifts === 'string' && gifts.trim()) return [gifts];
    return [];
  }

  function isNonEmptyRulesObject(obj) {
    return Boolean(obj && typeof obj === 'object' && Object.keys(obj).length > 0);
  }

  /**
   * A rule with no from_date/due_date is always active. Two shapes reach this:
   * - gift_auto_add_rule entries: whole days, from_date from `T00:00:00` and due_date
   *   through `T23:59:59`, formatted that way by the theme.
   * - the Promotion (snippets/promotion-gift.liquid): exact instants as epoch
   *   milliseconds, so the gift starts and stops at the same moment as the banners.
   * `new Date()` takes both. Invalid/unparseable dates are treated as "no limit" on that
   * side so a bad value never accidentally hides an otherwise-valid rule.
   */
  function isRuleDateActive(rule) {
    const now = Date.now();

    const fromRaw = rule?.from_date;
    if (fromRaw) {
      const fromDate = new Date(fromRaw);
      if (!isNaN(fromDate.getTime()) && now < fromDate.getTime()) return false;
    }

    const dueRaw = rule?.due_date;
    if (dueRaw) {
      const dueDate = new Date(dueRaw);
      if (!isNaN(dueDate.getTime()) && now > dueDate.getTime()) return false;
    }

    return true;
  }

  /**
   * A metaobject field can reach the theme as a plain string or as the metafield
   * object around it, depending on how the definition was set up in that store.
   * Everything that reads a rule field goes through this so both shapes behave.
   */
  function ruleFieldValue(raw) {
    if (raw && typeof raw === 'object') {
      if (Array.isArray(raw)) return ruleFieldValue(raw[0]);
      return raw.value ?? raw.handle ?? '';
    }
    return raw ?? '';
  }

  /**
   * Whether a rule's "Gift rule based on" is the discount-code basis. The
   * metaobject definition's stored choice is not identical across stores
   * ("Discount Code", "Discount code", "discount_code"), and an exact-string test
   * silently dropped correctly configured rules: the coupon field ignored them
   * while the auto-evaluator treated them as automatic product rules.
   */
  function isDiscountCodeBasis(value) {
    return String(ruleFieldValue(value)).trim().toLowerCase().replace(/[^a-z]+/g, '') === 'discountcode';
  }

  /**
   * The Promotion's "All purchases" gift condition: every cart holding at least one
   * item that is not itself a gift gets the gift, with no threshold and no code.
   */
  function isAllPurchasesBasis(value) {
    return String(ruleFieldValue(value)).trim().toLowerCase().replace(/[^a-z]+/g, '') === 'allpurchases';
  }

  const Utils = {
    log: (message, ...args) => {
      if (CONFIG.DEBUG) {
        console.log(`ℹ️ GIFT EXTENSION: ${message}`, ...args);
      }
    },

    warn: (message, ...args) => {
      console.warn(`⚠️ GIFT EXTENSION: ${message}`, ...args);
    },

    error: (message, ...args) => {
      console.error(`❌ GIFT EXTENSION: ${message}`, ...args);
    },

    getProductHandleFromUrl: (url) => {
      if (!url || typeof url !== 'string') return '';
      try {
        const urlParts = url.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        const handleParts = lastPart.split('?');
        return handleParts[0] || '';
      } catch {
        return '';
      }
    }
  };

  const CART_DRAWER_SECTION = 'cart-drawer';
  const CART_ICON_SECTION = 'cart-icon-bubble';

  /**
   * Sections asked for on every cart write.
   *
   * The drawer alone was not enough: the header count bubble is its own section,
   * and nothing here ever re-rendered it, so a cart this extension changed left
   * the header advertising the old number — remove both lines and the icon still
   * read "2". norse-cart-ui.js requests exactly this pair for the same reason.
   */
  const CART_SECTIONS = `${CART_DRAWER_SECTION},${CART_ICON_SECTION}`;

  /**
   * The cart drawer's real section id.
   *
   * It lives in a section group, so the id is generated and is NOT the file name.
   * `?section_id=cart-drawer` still answers 200 but renders the section with no
   * placed-instance config — no blocks — which flattens the progress bar's
   * milestones down to its blockless fallback. Falls back to the file name.
   */
  function resolveCartDrawerSectionId() {
    try {
      const host = document.querySelector('cart-drawer');
      const wrapper = host?.closest('[id^="shopify-section-"]');
      const id = wrapper?.id?.replace(/^shopify-section-/, '');
      return id || CART_DRAWER_SECTION;
    } catch {
      return CART_DRAWER_SECTION;
    }
  }

  /**
   * Shopify `routes.cart_*_url` values are often `/cart/change` and `/cart/add` without `.js`.
   * Theme fetch() uses those paths; matching only `*.js` missed quantity updates and many add-to-cart flows.
   *
   * Not anchored to the start of the path (no leading `^`) because stores with URL-based
   * languages/markets prefix every route with a locale segment (e.g. `/fr/cart/add.js`,
   * `/en-us/cart/change`). Anchoring to `^/cart/...` missed all of those requests, so the
   * gift re-check never ran for non-default-locale storefronts.
   */
  function isShopifyCartMutationPost(urlString, method) {
    if (String(method || 'GET').toUpperCase() !== 'POST') return false;
    let pathname = '';
    try {
      pathname = new URL(urlString, window.location.href).pathname;
    } catch {
      pathname = String(urlString || '').split('?')[0];
    }
    return /\/cart\/(add|change|update)(\.js)?$/.test(pathname);
  }

  const CartService = {
    async getCart() {
      window._giftExtensionFetching = true;
      try {
        const response = await fetch(CONFIG.CART_ENDPOINTS.GET);
        if (!response.ok) return null;
        const cart = await response.json();
        // Every gift-side cart read doubles as the view of the cart that the
        // checkout guard and the removal interceptors decide from, so neither
        // trails the evaluation that caused it.
        lastKnownCart = cart;
        CheckoutGuard.noteCart(cart);
        return cart;
      } catch (error) {
        Utils.error('Failed to fetch cart:', error);
        return null;
      } finally {
        window._giftExtensionFetching = false;
      }
    },

    async getCartWithSections() {
      window._giftExtensionFetching = true;
      try {
        const response = await fetch(`${CONFIG.CART_ENDPOINTS.GET}?sections=${CART_SECTIONS}`);
        return response.ok ? await response.json() : null;
      } catch (error) {
        Utils.error('Failed to fetch cart:', error);
        return null;
      } finally {
        window._giftExtensionFetching = false;
      }
    },

    async addItem(variantId, quantity = 1, properties = {}) {
      return this.request(CONFIG.CART_ENDPOINTS.ADD, {
        id: variantId,
        quantity,
        properties,
        sections: CART_SECTIONS
      }, 'Failed to add item to cart');
    },

    async addSingleItem(itemData) {
      return this.request(CONFIG.CART_ENDPOINTS.ADD, {
        ...itemData,
        sections: CART_SECTIONS
      }, 'Failed to add item to cart');
    },

    async addMultipleItems(items) {
      return this.request(CONFIG.CART_ENDPOINTS.ADD, {
        items,
        sections: CART_SECTIONS
      }, 'Failed to add items to cart');
    },

    async updateItems(updates) {
      return this.request(CONFIG.CART_ENDPOINTS.UPDATE, {
        updates,
        sections: CART_SECTIONS
      }, 'Failed to update cart');
    },

    async request(endpoint, body, errorMessage) {
      window._giftExtensionFetching = true;
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify(body)
        });
        
        if (!response.ok) {
          let message = errorMessage;
          try {
            const text = await response.text();
            if (text?.trim()) {
              try {
                const errorData = JSON.parse(text);
                message = errorData.description || errorData.message || text.trim();
              } catch {
                message = text.trim();
              }
            }
          } catch { /* ignore */ }
          throw new Error(message);
        }
        
        return await response.json();
      } catch (error) {
        Utils.error(errorMessage, error);
        throw error;
      } finally {
        window._giftExtensionFetching = false;
      }
    }
  };

  const ProductService = {
    cache: new Map(),

    async getVariantId(handle) {
      const raw = String(handle || '').trim();
      if (raw.includes('gid://shopify/Product/')) {
        Utils.warn('Received GID format instead of handle:', handle);
        throw new Error('Invalid gift handle format: GID detected. Use a product handle.');
      }
      const cleanHandle = raw.replace(/^\/+|\/+$/g, '');
      if (!cleanHandle) {
        throw new Error('Empty or invalid product handle');
      }

      if (this.cache.has(cleanHandle)) {
        return await this.cache.get(cleanHandle);
      }

      const fetchPromise = this.fetchVariantId(cleanHandle);
      this.cache.set(cleanHandle, fetchPromise);

      try {
        return await fetchPromise;
      } catch (error) {
        this.cache.delete(cleanHandle);
        throw error;
      }
    },

    async fetchVariantId(cleanHandle) {
      const response = await fetch(`/products/${cleanHandle}.js`);
      if (!response.ok) {
        throw new Error(`Product not found: ${cleanHandle}`);
      }

      const product = await response.json();
      if (!product?.variants?.length) {
        throw new Error(`No variants found for product: ${cleanHandle}`);
      }

      this.products.set(cleanHandle, product);

      // Stock check. /products/<handle>.js answers in the shopper's market, and
      // availability is per market (Canada sells from Darwynn only), so a gift can
      // be out here and in elsewhere. No available variant → null: the gift is
      // skipped and a rule's next handle — a Promotion tier's fallback — is tried.
      // `available` missing (an older response shape) counts as available.
      const variant = product.variants.find((v) => v.available !== false);
      return variant ? variant.id : null;
    },

    /**
     * The whole product, not just its first variant id. Needed to price a line
     * that is about to be added but is not in the cart yet — see PendingAdd.
     * Shares the variant-id cache's fetch so a PDP costs one request, not two.
     */
    products: new Map(),

    async getProduct(handle) {
      const cleanHandle = String(handle || '').trim().replace(/^\/+|\/+$/g, '');
      if (!cleanHandle) return null;
      if (this.products.has(cleanHandle)) return this.products.get(cleanHandle);

      try {
        await this.getVariantId(cleanHandle);
      } catch (error) {
        Utils.warn('Could not load product for pricing:', cleanHandle, error);
        return null;
      }

      return this.products.get(cleanHandle) || null;
    },

    /** Unit price of a variant, honouring a selling plan's own allocation. */
    variantUnitPrice(product, variantId, sellingPlanId) {
      const variant = (product?.variants || []).find(
        (candidate) => String(candidate.id) === String(variantId)
      );
      if (!variant) return null;

      if (sellingPlanId) {
        const allocation = (variant.selling_plan_allocations || []).find(
          (candidate) => String(candidate.selling_plan_id) === String(sellingPlanId)
        );
        // A subscription line is cheaper than the one-time price, so pricing it at
        // the one-time figure would promise a gift the cart then takes away again.
        if (allocation && allocation.price >= 0) return allocation.price;
      }

      return typeof variant.price === 'number' ? variant.price : null;
    },

    clearCache() {
      this.cache.clear();
      this.products.clear();
    }
  };

  /**
   * What the shopper is adding, recorded at submit time so the fetch interceptor
   * can price it a few milliseconds later.
   *
   * A POST to /cart/add carries a variant id and nothing else useful: no handle,
   * so no price, so no way to tell whether this add is the one that crosses the
   * threshold. The form that produced it knows — the extension already stamps
   * `data-gift-trigger="<handle>"` on add-to-cart buttons — so the handle is
   * picked up there and handed forward.
   *
   * Deliberately short-lived and matched on variant id: a stale context would
   * price the wrong line, and mispricing upward invents a gift the evaluator then
   * has to take back out of the shopper's cart.
   */
  const PendingAdd = {
    MAX_AGE_MS: 3000,
    _context: null,

    note(context) {
      if (!context?.variantId) return;
      this._context = { ...context, at: Date.now() };
    },

    take(variantId) {
      const context = this._context;
      if (!context) return null;
      if (Date.now() - context.at > this.MAX_AGE_MS) {
        this._context = null;
        return null;
      }
      if (String(context.variantId) !== String(variantId)) return null;
      return context;
    },

    /** Best-effort product handle for the form being submitted. */
    handleFromForm(form) {
      if (!form) return '';

      const stamped = form.querySelector('[data-gift-trigger]');
      const fromButton = stamped?.getAttribute('data-gift-trigger');
      if (fromButton) return fromButton;

      const declared = form.querySelector('[data-product-handle]');
      const fromAttribute = declared?.getAttribute('data-product-handle');
      if (fromAttribute) return fromAttribute;

      const link = form.querySelector('a[href*="/products/"]');
      if (link) {
        const fromLink = Utils.getProductHandleFromUrl(link.getAttribute('href'));
        if (fromLink) return fromLink;
      }

      // Last resort, and only on a PDP: the page's own product. Guarded by the
      // variant check in take(), so a mismatch simply declines the optimisation.
      const meta = window.ShopifyAnalytics?.meta?.product;
      if (meta?.handle) return meta.handle;

      return '';
    }
  };

  /**
   * Folds the earned gift into the shopper's own add-to-cart request.
   *
   * Without this every qualifying add is two round trips: the theme adds the
   * product, the evaluator notices the cart crossed the threshold half a second
   * later, and the gift arrives in a second request. The shopper watches the
   * drawer open with the wrong contents and then change under them.
   *
   * Predicting is what makes one request possible, and the prediction can only be
   * wrong in one direction that matters. Over-estimate and a gift is added that
   * the evaluator removes a moment later — visible, but no worse than a shopper
   * who removes a line. Under-estimate, or fail to resolve anything, and we simply
   * do not touch the request: that is exactly today's two-request behaviour, which
   * is why every branch here bails out rather than guessing.
   */
  const AddInterceptor = {
    /**
     * Reads an outgoing /cart/add body without consuming it.
     * Returns null for anything that is not a plain single-item add — the
     * multi-item bodies belong to the bundle and quiz builders, which compose
     * their own gift lines and must not be rewritten.
     */
    parseSingleItemBody(body) {
      try {
        if (body instanceof FormData || body instanceof URLSearchParams) {
          if ([...body.keys()].some((key) => key.startsWith('items['))) return null;
          const id = body.get('id');
          if (!id) return null;

          const properties = {};
          body.forEach((value, key) => {
            const match = key.match(/^properties\[(.+)\]$/);
            if (match) properties[match[1]] = value;
          });

          return {
            kind: body instanceof FormData ? 'formdata' : 'urlencoded',
            id,
            quantity: parseInt(body.get('quantity'), 10) || 1,
            sellingPlan: body.get('selling_plan') || null,
            properties,
            sections: body.get('sections'),
            sectionsUrl: body.get('sections_url')
          };
        }

        if (typeof body === 'string') {
          const parsed = JSON.parse(body);
          if (Array.isArray(parsed?.items)) return null;
          if (!parsed?.id) return null;

          return {
            kind: 'json',
            id: parsed.id,
            quantity: parseInt(parsed.quantity, 10) || 1,
            sellingPlan: parsed.selling_plan || null,
            properties: parsed.properties || {},
            sections: parsed.sections,
            sectionsUrl: parsed.sections_url
          };
        }
      } catch (error) {
        Utils.warn('Could not read the add-to-cart body, leaving it alone:', error);
      }

      return null;
    },

    /**
     * The gifts this add would earn, or null. Evaluated against the cart as it
     * WILL be — current lines plus the one in flight — so a threshold rule can be
     * answered before the line exists.
     */
    async giftForPendingAdd(parsed) {
      const manager = window.giftManager?.instance?.();
      if (!manager?.evaluator) return null;

      // A line already carrying a gift flag is the extension's own work, or a
      // bundle's. Either way it is not a shopper adding a product.
      if (isAnyGiftItem({ properties: parsed.properties })) return null;

      const context = PendingAdd.take(parsed.id);
      if (!context?.handle) return null;

      const product = await ProductService.getProduct(context.handle);
      if (!product) return null;

      const unitPrice = ProductService.variantUnitPrice(product, parsed.id, parsed.sellingPlan);
      if (unitPrice === null) return null;

      const current = lastKnownCart?.items;
      if (!Array.isArray(current)) return null;

      const linePrice = unitPrice * parsed.quantity;
      const projected = [
        ...current,
        {
          key: '__pending_add__',
          url: `/products/${context.handle}`,
          quantity: parsed.quantity,
          price: unitPrice,
          final_price: unitPrice,
          line_price: linePrice,
          final_line_price: linePrice,
          properties: parsed.properties,
          ...(parsed.sellingPlan && {
            selling_plan_allocation: { selling_plan: { id: parsed.sellingPlan } }
          })
        }
      ];

      const evaluation = await manager.evaluator.evaluate(projected);

      // Every gift this add earns (several when Promotion tiers stack). One already
      // in the cart is left out: adding it again would stack a second line that
      // buildDiscountGiftNormalizationUpdates would then have to unpick.
      const gifts = (evaluation?.shouldAdd || []).filter((gift) =>
        gift?.variantId &&
        !current.some((item) => isGiftItem(item) && String(item.variant_id) === String(gift.variantId))
      );
      return gifts.length > 0 ? gifts : null;
    },

    /**
     * Rebuilds the request as the shopper's item plus the earned gifts, preserving whatever section
     * rendering the caller asked for.
     */
    buildCombinedBody(parsed, gifts) {
      const items = [
        {
          id: parsed.id,
          quantity: parsed.quantity,
          ...(Object.keys(parsed.properties || {}).length > 0 && { properties: parsed.properties }),
          ...(parsed.sellingPlan && { selling_plan: parsed.sellingPlan })
        },
        ...gifts.map((gift) => ({
          id: gift.variantId,
          quantity: 1,
          properties: { [CONFIG.GIFT_PROPERTY]: 'true' }
        }))
      ];

      return JSON.stringify({
        items,
        ...(parsed.sections && { sections: parsed.sections }),
        ...(parsed.sectionsUrl && { sections_url: parsed.sectionsUrl })
      });
    },

    /**
     * Makes a two-item response look like the one-item response the caller asked
     * for: its own item's fields at the top level, sections untouched.
     *
     * Without this the theme's own add-to-cart handler, which reads the added
     * item straight off the response, would find an object shaped nothing like
     * what /cart/add.js returns for a single item.
     */
    reshapeResponse(payload) {
      const added = payload?.items?.[0];
      if (!added) return payload;

      return {
        ...added,
        items: payload.items,
        ...(payload.sections && { sections: payload.sections })
      };
    }
  };

  const GiftRulesManager = {
    buildGiftToTriggerMapping(giftRules) {
      const mapping = new Map();
      
      Object.values(giftRules).forEach(rule => {
        if (!rule?.trigger_handles?.length || !rule?.gifts) return;
        
        normalizeGiftHandles(rule.gifts).forEach(giftHandle => {
          if (!giftHandle) return;
          
          if (!mapping.has(giftHandle)) {
            mapping.set(giftHandle, new Set());
          }
          
          rule.trigger_handles.forEach(triggerHandle => {
            mapping.get(giftHandle).add(triggerHandle);
          });
        });
      });
      
      return mapping;
    },

    findConnectedGifts(productHandle, cartItems, itemToRemove, giftToTriggerMapping) {
      return cartItems
        .filter(item => isGiftItem(item))
        .filter(item => {
          const giftHandle = Utils.getProductHandleFromUrl(item.url);
          if (!giftHandle) return false;
          
          const connectedTriggers = giftToTriggerMapping.get(giftHandle);
          if (!connectedTriggers || !connectedTriggers.has(productHandle)) {
            return false;
          }
          
          const remainingTriggers = cartItems.filter(cartItem => 
            !isGiftItem(cartItem) &&
            cartItem.key !== item.key &&
            cartItem.key !== itemToRemove.key &&
            connectedTriggers.has(Utils.getProductHandleFromUrl(cartItem.url))
          );
          
          return remainingTriggers.length === 0;
        });
    }
  };

  const CartStateTracker = {
    createSignature(cart) {
      return JSON.stringify({
        item_count: cart.item_count,
        total_price: cart.total_price,
        items: cart.items?.map(item => `${item.key}:${item.quantity}`).sort().join(',')
      });
    },

    isIncomplete(cart) {
      return cart && (
        !cart.hasOwnProperty('item_count') || 
        cart.item_count === undefined ||
        !cart.hasOwnProperty('total_price') ||
        cart.total_price === undefined ||
        (cart.items?.length > 0 && !cart.total_price)
      );
    }
  };

  const CartSectionService = {
    async fetchCartSections(cart) {
      if (!cart) return cart;
      if (cart.sections?.[CART_DRAWER_SECTION]) return cart;
      
      const cartUrl = window.themeVariables?.routes?.cartUrl;
      if (!cartUrl) return cart;

      try {
        // The placed instance's id, not the file name — see resolveCartDrawerSectionId().
        const sectionId = resolveCartDrawerSectionId();
        const response = await fetch(`${cartUrl}?section_id=${encodeURIComponent(sectionId)}`);
        if (!response.ok) return cart;
        
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const sectionRoot =
          doc.querySelector(`[id="shopify-section-${CART_DRAWER_SECTION}"]`) ||
          doc.querySelector(`[id$="__${CART_DRAWER_SECTION}"]`) ||
          doc.querySelector(`[id*="__${CART_DRAWER_SECTION}"]`);
        
        if (sectionRoot) {
          cart.sections = cart.sections || {};
          cart.sections[CART_DRAWER_SECTION] = sectionRoot.innerHTML;
        }
      } catch (error) {
        Utils.warn('Could not fetch cart sections:', error);
      }
      
      return cart;
    },

    mergeSections(targetCart, sourceWithSections) {
      if (!targetCart || !sourceWithSections?.sections) return targetCart;
      targetCart.sections = { ...targetCart.sections, ...sourceWithSections.sections };
      return targetCart;
    }
  };

  /**
   * Ensures cart JSON includes rendered `cart-drawer` section HTML for theme hooks.
   * @param {object|null|undefined} cart
   * @param {{ sections?: Record<string, string> }|null|undefined} responseWithSections
   */
  async function enrichCartWithSections(cart, responseWithSections = null) {
    if (!cart) return null;
    if (responseWithSections?.sections) {
      CartSectionService.mergeSections(cart, responseWithSections);
    }
    if (!cart.sections?.[CART_DRAWER_SECTION]) {
      await CartSectionService.fetchCartSections(cart);
    }
    return cart;
  }

  /**
   * Repaint the header cart icon from the freshly rendered section.
   *
   * There are TWO `#cart-icon-bubble` nodes on a storefront page — header.liquid
   * and norse-header.liquid each render one — so this must write to all of them,
   * not the first match.
   *
   * Prefers the section that rode along with the cart write; falls back to
   * fetching it. The fallback matters on the paths that reach the cart through
   * something other than this extension's own CartService.
   */
  async function refreshCartIconBubble(cart) {
    try {
      let markup = cart?.sections?.[CART_ICON_SECTION];

      if (!markup) {
        const response = await fetch(`?section_id=${CART_ICON_SECTION}`);
        if (!response.ok) return false;
        markup = await response.text();
      }

      const doc = new DOMParser().parseFromString(markup, 'text/html');
      const fresh =
        doc.getElementById(`shopify-section-${CART_ICON_SECTION}`) ||
        doc.getElementById('cart-icon-bubble') ||
        doc.body;

      if (!fresh) return false;

      const targets = document.querySelectorAll('[id="cart-icon-bubble"]');
      if (targets.length === 0) return false;

      targets.forEach((target) => {
        target.innerHTML = fresh.innerHTML;
      });

      return true;
    } catch (error) {
      Utils.warn('Could not refresh the cart icon bubble:', error);
      return false;
    }
  }

  /**
   * Delegate cart drawer DOM refresh to the shared NorseCartUI module.
   * Falls back to a basic fetch-replace if the module isn't loaded yet.
   */
  async function refreshCartDrawerDom() {
    try {
      if (window.NorseCartUI && typeof window.NorseCartUI.refreshCartDrawer === 'function') {
        await window.NorseCartUI.refreshCartDrawer();
        return true;
      }
      return false;
    } catch (error) {
      Utils.warn('Cart drawer DOM refresh failed:', error);
      return false;
    }
  }

  const EventDispatcher = {
    dispatchCartEvents(cart) {
      refreshCartIconBubble(cart);
      ['cart:refresh', 'cart:updated', 'cart:refreshCartNeeded'].forEach(eventName => {
        document.documentElement.dispatchEvent(new CustomEvent(eventName, {
          bubbles: true,
          detail: { cart }
        }));
      });
    },

    dispatchCartEventsSmooth(cart) {
      if (!cart) return;

      // Every write path in this file funnels through here, which makes it the one
      // place that guarantees the header count matches the cart it just wrote.
      refreshCartIconBubble(cart);

      requestAnimationFrame(() => {
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart }
        }));
      });
      
      requestAnimationFrame(() => {
        setTimeout(() => {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: {
              cart,
              replacementDelay: 150,
              openMiniCart: false
            }
          }));
        }, 50);
      });
    },

    dispatchGiftEvent(eventName, detail) {
      document.dispatchEvent(new CustomEvent(CONFIG.EVENTS[eventName], {
        bubbles: true,
        detail
      }));
    }
  };

  /**
   * Checkout guard.
   *
   * A gift is a real variant that happens to cost 0, so a cart holding nothing but
   * gift lines is a purchasable $0 order. Gift removal can only run after /cart.js
   * answers, which leaves a window where the shopper has already dropped the line
   * that earned the gift and the checkout button is still live. Clicking through
   * that window buys the gift on its own.
   *
   * Two layers, and only the second one is load-bearing:
   *
   *  1. Checkout controls are disabled while the cart is gift-only or an evaluation
   *     is in flight. Cosmetic only — the drawer replaces its own markup on every
   *     cart update, so this state gets repainted away regularly.
   *  2. Every checkout click and submit is intercepted in the capture phase and
   *     re-validated against a fresh /cart.js before the navigation is allowed.
   *     Document-level capture listeners survive the repaints, so this is the layer
   *     that actually holds.
   *
   * Neither layer stops a hand-written POST to /cart/add.js followed by /checkout —
   * nothing in the theme can. Closing that needs a Cart and Checkout Validation
   * function in the app.
   */
  const CheckoutGuard = {
    CONTROL_SELECTOR: 'button[name="checkout"], input[name="checkout"], a[href*="/checkout"]',
    BLOCKED_ATTR: 'data-gift-checkout-blocked',
    VERIFIED_ATTR: 'data-gift-checkout-verified',
    WENT_TO_CHECKOUT_KEY: 'gift:wentToCheckout',

    _lastCart: null,
    _pending: false,
    _validating: false,

    init() {
      if (this._initialized) return;
      this._initialized = true;

      this._onIntent = (event) => this.onCheckoutIntent(event);
      this._onCartEvent = (event) => this.noteCart(event?.detail?.cart || null);

      document.addEventListener('click', this._onIntent, true);
      document.addEventListener('submit', this._onIntent, true);

      ['cart:updated', 'cart:refresh', CONFIG.EVENTS.CART_UPDATED].forEach((eventName) => {
        document.addEventListener(eventName, this._onCartEvent);
        document.documentElement.addEventListener(eventName, this._onCartEvent);
      });

      this.sync();
    },

    /**
     * How long the cart may stay "unverified" before the guard gives up and
     * re-enables checkout on its own. A dropped promise anywhere in the
     * evaluation chain would otherwise leave a permanently dead checkout button,
     * which costs more than the $0 order this guard exists to stop.
     */
    PENDING_MAX_MS: 4000,

    /**
     * How long an approved click stays approved. Long enough to cover the
     * click → submit → navigation chain, short enough that it cannot be reused
     * by a later click on a cart that has changed since.
     */
    VERIFIED_WINDOW_MS: 1000,

    setPending(isPending) {
      clearTimeout(this._pendingTimer);

      // With no gift rules loaded (promo off, outside its date window) nothing
      // can strand a gift, and nothing would ever clear the flag either.
      if (isPending && !this.isArmed()) return;

      this._pending = !!isPending;

      if (this._pending) {
        this._pendingTimer = setTimeout(() => {
          this._pending = false;
        }, this.PENDING_MAX_MS);
      }
    },

    isArmed() {
      const rules = window.giftManager?.instance?.()?.giftRules || window.GIFT_RULES;
      return !!rules && Object.keys(rules).length > 0;
    },

    noteCart(cart) {
      if (cart) this._lastCart = cart;
      this.sync();
    },

    /** A cart is checkout-worthy only if something in it is not a gift. */
    hasPurchasableItems(items) {
      return Array.isArray(items) && items.some((item) => !isAnyGiftItem(item));
    },

    /**
     * Only the settled, certain case — a cart whose every line is a gift.
     *
     * Deliberately NOT true merely because a mutation is in flight. #CartDrawer-Checkout
     * is a .cta-button, and base.css greys out [aria-disabled='true'], so marking the
     * pending window would flash the checkout button grey on every single add to cart.
     * The pending window is handled where it belongs: awaitSettled(), inside the click.
     */
    isCartBlocked() {
      if (!this._lastCart) return false;
      const items = this._lastCart.items;
      if (!Array.isArray(items) || items.length === 0) return false;
      return !this.hasPurchasableItems(items);
    },

    /**
     * Never sets the native `disabled` property.
     *
     * A disabled button fires no events at all, so the click would be swallowed
     * before the interceptor below ever saw it: the shopper presses checkout,
     * nothing happens, and nothing explains why. Worse, it is exactly backwards —
     * the moments this guard most wants to inspect a click are the moments the
     * button would have been disabled. Mark it up as unavailable, let the click
     * land, and let validateAndProceed() be the one that decides.
     */
    sync() {
      const blocked = this.isCartBlocked();

      document.querySelectorAll(this.CONTROL_SELECTOR).forEach((control) => {
        if (blocked) {
          if (control.hasAttribute(this.BLOCKED_ATTR)) return;
          control.setAttribute(this.BLOCKED_ATTR, 'true');
          control.setAttribute('aria-disabled', 'true');
          return;
        }

        // Only clear what this guard set. An empty cart ships `aria-disabled`
        // straight from Liquid, and clearing that would advertise a checkout
        // button with nothing behind it.
        if (!control.hasAttribute(this.BLOCKED_ATTR)) return;
        control.removeAttribute(this.BLOCKED_ATTR);
        control.removeAttribute('aria-disabled');
      });

      // Accelerated buttons (Shop Pay, PayPal) render inside an iframe, so their
      // clicks are unreachable. Covering the container is the only lever there is.
      document.querySelectorAll('.additional-checkout-buttons').forEach((wrapper) => {
        wrapper.style.pointerEvents = blocked ? 'none' : '';
        wrapper.style.opacity = blocked ? '0.5' : '';
      });
    },

    findControl(event) {
      if (event.type === 'submit') {
        const form = event.target;
        if (!form?.tagName || form.tagName !== 'FORM') return null;
        const submitter = event.submitter;
        if (submitter?.name === 'checkout') return submitter;

        // Button first, deliberately. mini-cart carries BOTH a hidden
        // input[name="checkout"] (so a programmatic submit keeps the param) and
        // the real button, and the hidden input comes first in the markup — a
        // plain querySelector for either would keep picking the one that cannot
        // be clicked.
        return form.querySelector('button[name="checkout"]')
          || form.querySelector('input[name="checkout"]');
      }

      const target = event.target;
      if (!target?.closest) return null;
      return target.closest(this.CONTROL_SELECTOR);
    },

    onCheckoutIntent(event) {
      // No rules loaded means no gift can be stranded. Leave checkout alone rather
      // than putting a /cart.js roundtrip in front of every click year-round.
      if (!this.isArmed()) return;

      const control = this.findControl(event);
      if (!control) return;

      // Our own replay of the shopper's click, once the cart checked out clean.
      //
      // The flag is cleared on a later task rather than here: clicking a submit
      // button fires `click` AND then `submit`, both land on this handler, and
      // consuming the flag on the click left the submit looking unverified — so
      // the guard cancelled the very navigation it had just approved.
      if (control.hasAttribute(this.VERIFIED_ATTR)) {
        clearTimeout(this._verifiedTimer);
        this._verifiedTimer = setTimeout(() => {
          control.removeAttribute(this.VERIFIED_ATTR);
        }, this.VERIFIED_WINDOW_MS);
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.validateAndProceed(control);
    },

    async validateAndProceed(control) {
      if (this._validating) return;
      this._validating = true;
      this.setLoading(control, true);

      // interceptCheckoutNavigation() sets this flag from its own capture listener
      // on the same click, and processCartUpdate() reads it as "the shopper came
      // back from checkout, strip every gift". Left in place it would delete a
      // legitimately earned gift during the very check that is about to wave the
      // shopper through. Clearing it is safe: replaying the click below re-fires
      // that listener and sets it again, this time against a real navigation.
      try { sessionStorage.removeItem(this.WENT_TO_CHECKOUT_KEY); } catch (_) {}

      try {
        // Let any cart mutation already in flight land first. Reading /cart.js
        // while a /cart/change is still travelling returns the cart as it was a
        // moment ago — the trigger still in it, the gift still earned — and the
        // guard waves through precisely the click it exists to catch. Removing a
        // line and hitting checkout 80ms later does exactly this.
        await this.awaitSettled();

        const manager = window.giftManager?.instance?.();
        if (manager?.forceEvaluate) {
          await manager.forceEvaluate();
        }

        const cart = await CartService.getCart();
        this._lastCart = cart || this._lastCart;

        // Fail open. A cart fetch that never answers is a network fault, and
        // blocking every checkout behind it costs far more than the rare $0 order
        // it would stop — the validation function is what closes this for real.
        if (!cart || this.hasPurchasableItems(cart.items)) {
          this.setLoading(control, false);
          this.proceed(control);
          return;
        }

        // Gift-only cart. forceEvaluate() has already dropped the orphaned gift
        // lines, so repaint and stay put: the shopper sees an empty cart rather
        // than a $0 order.
        Utils.log('Checkout blocked: cart holds gift lines only');
        await refreshCartDrawerDom();
        EventDispatcher.dispatchCartEventsSmooth(cart);
      } catch (error) {
        Utils.error('Checkout guard validation failed, letting the shopper through:', error);
        this.setLoading(control, false);
        this.proceed(control);
        return;
      } finally {
        this._validating = false;
        this.setLoading(control, false);
        this.sync();
      }
    },

    /**
     * Block until no cart mutation is outstanding. Bounded by PENDING_MAX_MS on
     * the flag itself, so this cannot wait forever on a request that never lands.
     */
    async awaitSettled(maxMs = 5000) {
      const startedAt = Date.now();
      while (this._pending && (Date.now() - startedAt) < maxMs) {
        await sleep(60);
      }
    },

    /**
     * Replay the shopper's click, now that the cart has been checked.
     *
     * The cart came back verified, so the pending flag is dropped first and the
     * control's blocked markup with it — the shopper is on their way out and must
     * not be shown a checkout button that reads as unavailable.
     */
    proceed(control) {
      clearTimeout(this._pendingTimer);
      this._pending = false;
      this.sync();

      if (control.hasAttribute(this.BLOCKED_ATTR)) {
        control.removeAttribute(this.BLOCKED_ATTR);
        control.removeAttribute('aria-disabled');
      }

      control.setAttribute(this.VERIFIED_ATTR, '1');

      // A hidden input[name="checkout"] is a form field, not a control: clicking
      // it does nothing. Submit the form it belongs to instead, which is exactly
      // what that field is there to make work.
      if (control.tagName === 'INPUT' && control.type === 'hidden') {
        const form = control.form || control.closest('form');
        if (form) {
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
          else form.submit();
        }
        return;
      }

      control.click();
    },

    setLoading(control, isLoading) {
      if (!control?.classList) return;
      control.classList.toggle('loading', isLoading);
      control.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      if (!isLoading) control.removeAttribute('aria-busy');
    }
  };

  class Debouncer {
    constructor(delay) {
      this.delay = delay;
      this.timeout = null;
      this.pendingValue = null;
    }

    debounce(fn) {
      return (value) => {
        this.pendingValue = value;

        if (this.timeout) {
          clearTimeout(this.timeout);
          this.timeout = null;
        }

        // Hand the superseded caller its resolution instead of leaving it hanging:
        // the newer run covers its work, and callers release the checkout guard in
        // a finally block once their call settles.
        const superseded = this.pendingResolve;
        this.pendingResolve = null;
        if (typeof superseded === 'function') superseded();

        return new Promise((resolve) => {
          this.pendingResolve = resolve;
          this.timeout = setTimeout(async () => {
            const valueToProcess = this.pendingValue;
            this.pendingValue = null;
            this.timeout = null;
            this.pendingResolve = null;
            await fn(valueToProcess);
            resolve();
          }, this.delay);
        });
      };
    }

    /**
     * Drop a scheduled run. Resolves the promise the cancelled call handed out —
     * every caller awaits checkAndApplyGifts(), so leaving it dangling would hang
     * the checkout guard on the very click it is meant to unblock.
     */
    cancel() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
      this.pendingValue = null;
      const resolve = this.pendingResolve;
      this.pendingResolve = null;
      if (typeof resolve === 'function') resolve();
    }
  }

  class GiftRuleEvaluator {
    constructor(giftRules, matchesPurchaseType) {
      this.giftRules = giftRules;
      this.matchesPurchaseType = matchesPurchaseType;
    }

    async evaluate(cartItems) {
      const result = {
        shouldAdd: [],
        shouldRemove: [],
        currentGifts: []
      };
      if (!Array.isArray(cartItems)) return result;

      result.currentGifts = cartItems.filter(isGiftItem);

      const analysis = this.analyzeCartItems(cartItems);
      // Use merchandise subtotal excluding auto-gift lines only. cart.total_price includes gift
      // line amounts, so using it here keeps price thresholds "stuck" and gifts never remove.
      const cartTotalInDollars = analysis.cartTotalInDollars;

      const {
        handleToCount,
        handleHasSubscription,
        currentGiftVariantIds,
        hasSubscriptionInCart
      } = analysis;

      let giftRulesArray = Object.values(this.giftRules || {});
      if (giftRulesArray.length === 0) return result;

      const purchaseType = hasSubscriptionInCart ? 'subscription' : 'onetime';
      giftRulesArray = this.sortRulesByPriority(giftRulesArray, cartTotalInDollars);

      /*
        Two ways a rule can grant its gift:
        - `stack: true` (a Promotion with "Give every tier reached" on): every such
          rule that matches adds its own gift, so a cart past two tiers holds both.
        - everything else (legacy gift_auto_add_rule entries, a Promotion with
          stacking off): as before, only the highest-priority match — the highest
          threshold reached — grants one gift.
        A gift is granted once however many rules name it.
      */
      const selectedGifts = [];
      const addSelected = (gift) => {
        if (gift && !selectedGifts.some((g) => g.variantId === gift.variantId)) selectedGifts.push(gift);
      };

      for (const rule of giftRulesArray.filter((r) => r?.stack === true)) {
        addSelected(await this.selectPriorityGift(
          [rule],
          handleToCount,
          handleHasSubscription,
          cartTotalInDollars,
          hasSubscriptionInCart,
          purchaseType,
          currentGiftVariantIds
        ));
      }

      addSelected(await this.selectPriorityGift(
        giftRulesArray.filter((r) => r?.stack !== true),
        handleToCount,
        handleHasSubscription,
        cartTotalInDollars,
        hasSubscriptionInCart,
        purchaseType,
        currentGiftVariantIds
      ));

      selectedGifts.forEach((gift) => {
        if (!currentGiftVariantIds.has(gift.variantId)) result.shouldAdd.push(gift);
      });

      result.currentGifts.forEach((giftItem) => {
        if (!giftItem?.key) return;

        const isSelectedGift = selectedGifts.some((gift) => giftItem.variant_id === gift.variantId);
        if (isSelectedGift) {
          if (giftItem.quantity > 1) {
            result.shouldRemove.push({
              ...giftItem,
              updateQuantity: 1
            });
          }
          return;
        }

        result.shouldRemove.push(giftItem);
      });

      return result;
    }

    async selectPriorityGift(
      giftRulesArray,
      handleToCount,
      handleHasSubscription,
      cartTotalInDollars,
      hasSubscriptionInCart,
      purchaseType,
      currentGiftVariantIds
    ) {
      for (const rule of giftRulesArray) {
        try {
          const ruleBasedOn = rule?.gift_rule_based_on || 'Product';
          const matched = this.checkRuleMatch(
            rule,
            ruleBasedOn,
            handleToCount,
            handleHasSubscription,
            cartTotalInDollars,
            hasSubscriptionInCart
          );

          if (!matched) continue;
          if (!this.matchesPurchaseType(rule.purchase_type, purchaseType)) continue;

          const giftHandles = normalizeGiftHandles(rule.gifts).filter(handle => handle?.trim());
          if (giftHandles.length === 0) continue;

          const candidates = [];
          for (const giftHandle of giftHandles) {
            try {
              const giftVariantId = await ProductService.getVariantId(giftHandle);
              if (!giftVariantId) continue;

              candidates.push({
                handle: giftHandle,
                variantId: giftVariantId,
                quantity: 1
              });
            } catch (error) {
              Utils.error('Error processing gift handle:', giftHandle, error);
            }
          }

          if (candidates.length === 0) continue;

          const existingCandidate = candidates.find(candidate =>
            currentGiftVariantIds.has(candidate.variantId)
          );

          return existingCandidate || candidates[0];
        } catch (error) {
          Utils.error('Error evaluating gift rule:', error);
        }
      }

      return null;
    }

    analyzeCartItems(cartItems) {
      const handleToCount = new Map();
      const handleHasSubscription = new Map();
      const currentGiftVariantIds = new Set();
      let cartTotalInCents = 0;
      let hasSubscriptionInCart = false;

      cartItems.forEach(item => {
        if (!item?.url) return;
        
        const handle = Utils.getProductHandleFromUrl(item.url);
        const isGift = isGiftItem(item);
        if (isGift) {
          if (typeof item.variant_id !== 'undefined') {
            currentGiftVariantIds.add(item.variant_id);
          }
          return;
        }

        const qty = item.quantity || 1;
        const itemLinePrice = item.final_line_price || item.line_price || 
                             ((item.final_price || item.price || 0) * qty);
        cartTotalInCents += itemLinePrice;
        
        handleToCount.set(handle, (handleToCount.get(handle) || 0) + qty);
        
        if (item.selling_plan_allocation?.selling_plan) {
          handleHasSubscription.set(handle, true);
          hasSubscriptionInCart = true;
        } else if (!handleHasSubscription.has(handle)) {
          handleHasSubscription.set(handle, false);
        }
      });

      return {
        handleToCount,
        handleHasSubscription,
        currentGiftVariantIds,
        cartTotalInDollars: cartTotalInCents / 100,
        hasSubscriptionInCart
      };
    }

    checkRuleMatch(rule, ruleBasedOn, handleToCount, handleHasSubscription, cartTotalInDollars, hasSubscriptionInCart) {
      if (isDiscountCodeBasis(ruleBasedOn)) return false;

      if (!isRuleDateActive(rule)) {
        Utils.log('Rule outside its from_date/due_date window, skipping:', rule?.from_date, rule?.due_date);
        return false;
      }

      if (isAllPurchasesBasis(ruleBasedOn)) {
        // analyzeCartItems() only counts non-gift lines into handleToCount.
        return handleToCount.size > 0;
      }

      if (ruleBasedOn === 'Price') {
        const rulePrice = rule?.price !== null && rule?.price !== undefined ? parseFloat(rule.price) : null;
        if (rulePrice === null || isNaN(rulePrice) || rulePrice <= 0) {
          Utils.warn('Invalid or missing price in rule:', rule);
          return false;
        }
        
        // Both sides are already in the presentment currency: cartTotalInDollars
        // comes from /cart.js line prices, and layout/theme.liquid converts each
        // rule's threshold with cart.currency.conversion_rate before emitting it
        // into window.GIFT_RULES. Do not convert again here.
        const matched = cartTotalInDollars >= rulePrice;
        Utils.log(`Price rule check: cartTotal=${cartTotalInDollars}, rulePrice=${rulePrice}, matched=${matched}`);
        return matched;
      }

      const triggers = rule?.trigger_handles || [];
      if (triggers.length === 0) return false;

      return triggers.some(trigger => {
        const count = handleToCount.get(trigger) || 0;
        return count > 0;
      });
    }

    sortRulesByPriority(rules, cartTotal) {
      const priceRules = [];
      const productRules = [];
      
      rules.forEach(rule => {
        const ruleBasedOn = rule?.gift_rule_based_on || 'Product';
        if (isDiscountCodeBasis(ruleBasedOn)) return;
        // "All purchases" is a Price rule at zero: it sorts after every real threshold.
        if (ruleBasedOn === 'Price' || isAllPurchasesBasis(ruleBasedOn)) {
          priceRules.push(rule);
        } else {
          productRules.push(rule);
        }
      });
      
      priceRules.sort((a, b) => {
        const priceA = parseFloat(a?.price) || 0;
        const priceB = parseFloat(b?.price) || 0;
        return priceB - priceA;
      });
      
      return [...priceRules, ...productRules];
    }
  }

  /*
    The drawer repaint lives on DiscountCodeWidget, and every existing caller of it
    is a discount-code flow (_applyGiftRuleCode, _applyStoreCode, _syncWithCartState,
    _normalizeGiftLines). A rule with no discount_code — every "Price" rule — never
    reaches any of them, so the gift landed in the cart while the open drawer kept
    showing the cart from before: the shopper only saw it after a reload.

    Guarded rather than called directly: DiscountCodeWidget is declared after this
    class, and the repaint is a nicety. If it is ever missing, the gift must still
    be added.
  */
  async function repaintCartDrawer() {
    try {
      if (typeof DiscountCodeWidget !== 'undefined' &&
          typeof DiscountCodeWidget._refreshCartDrawer === 'function') {
        await DiscountCodeWidget._refreshCartDrawer();
      }
    } catch (error) {
      Utils.warn('Could not repaint the cart drawer after a gift change:', error);
    }
  }

  /**
   * Sends the shopper's add together with the gift it earns, as one request, and
   * returns the Response. Returns null to mean "not our business" — the caller
   * then sends the original request untouched.
   *
   * Every failure path returns null rather than throwing. An add-to-cart that
   * breaks because a gift could not be predicted would be a far worse bug than
   * the second request this saves.
   */
  async function maybeAddGiftInSameRequest({
    urlString, method, options, isRequestObject, isInternal, originalFetch, context, args
  }) {
    if (isInternal || isRequestObject) return null;
    if (String(method).toUpperCase() !== 'POST') return null;
    if (!CheckoutGuard.isArmed()) return null;

    let pathname = '';
    try {
      pathname = new URL(urlString, window.location.href).pathname;
    } catch {
      return null;
    }
    if (!/\/cart\/add(\.js)?$/.test(pathname)) return null;

    try {
      const parsed = AddInterceptor.parseSingleItemBody(options?.body);
      if (!parsed) return null;

      const gifts = await AddInterceptor.giftForPendingAdd(parsed);
      if (!gifts) return null;

      Utils.log('Folding the earned gifts into the add request:', gifts.map((gift) => gift.handle));

      // originalFetch, not window.fetch: this must not re-enter the wrapper that
      // called us. _giftExtensionFetching additionally keeps the post-mutation
      // re-check from treating our own write as a shopper action.
      window._giftExtensionFetching = true;
      let response;
      try {
        response = await originalFetch.call(context, urlString, {
          ...options,
          method: 'POST',
          headers: { ...JSON_HEADERS },
          body: AddInterceptor.buildCombinedBody(parsed, gifts)
        });
      } finally {
        window._giftExtensionFetching = false;
      }

      // Anything the combined add rejects — a sold-out gift is the likely one —
      // must not take the shopper's product down with it. Send what they asked
      // for, unchanged.
      if (!response.ok) {
        Utils.warn('Combined add rejected, falling back to the original request');
        return await originalFetch.apply(context, args);
      }

      const payload = await response.json();
      const reshaped = AddInterceptor.reshapeResponse(payload);

      lastKnownCart = null;
      CheckoutGuard.setPending(true);

      return new Response(JSON.stringify(reshaped), {
        status: response.status,
        statusText: response.statusText,
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      Utils.warn('Could not fold the gift into the add request:', error);
      return null;
    }
  }

  class GiftManager {
    constructor() {
      this.giftRules = {};
      this.giftToTriggerMapping = new Map();
      this.isProcessing = false;
      this.initialized = false;
      this.lastCartState = null;
      this.debouncer = new Debouncer(CONFIG.DELAYS.CART_UPDATE);
      this.evaluator = null;
      this._onCartUpdate = this.handleCartUpdate.bind(this);
      this._onFormSubmit = this.handleFormSubmit.bind(this);

      this.init();
    }

    matchesPurchaseType(rulePurchaseType, targetPurchaseType) {
      if (!targetPurchaseType) return false;
      
      if (!rulePurchaseType || rulePurchaseType.trim() === '') {
        return true;
      }
      
      const ruleType = normalizePurchaseType(rulePurchaseType);
      const targetType = normalizePurchaseType(targetPurchaseType);
      
      if (ruleType === targetType) return true;
      
      const ruleTypes = ruleType.split(',').map(t => t.trim());
      return ruleTypes.includes(targetType);
    }

    init() {
      try {
        this.loadGiftRules();
        this.setupEventListeners();
        this.initialized = true;
        this.precacheGiftVariants();
      } catch (error) {
        Utils.error('Failed to initialize Gift Manager:', error);
      }
    }

    precacheGiftVariants() {
      if (!this.giftRules) return;
      for (const rule of Object.values(this.giftRules)) {
        for (const handle of normalizeGiftHandles(rule.gifts)) {
          if (!handle?.trim()) continue;
          ProductService.getVariantId(handle).catch(() => {});
        }
      }
    }

    loadGiftRules() {
      try {
        const giftRules = window.GIFT_RULES;
        if (!isNonEmptyRulesObject(giftRules)) {
          Utils.warn('No gift rules found or GIFT_RULES is empty');
          return false;
        }
        
        this.giftRules = this.parseGiftRules(giftRules);
        this.giftToTriggerMapping = GiftRulesManager.buildGiftToTriggerMapping(this.giftRules);
        this.evaluator = new GiftRuleEvaluator(this.giftRules, this.matchesPurchaseType.bind(this));

        // Rules can arrive after first init (gift-rules-ready / watcher). Keep the
        // discount-code widget in sync so late-loading rules still enable it.
        DiscountCodeWidget.init(giftRules);

        return true;
      } catch (error) {
        Utils.error('Error loading gift rules:', error);
        return false;
      }
    }

    parseGiftRules(giftRules) {
      const parsed = { ...giftRules };
      
      Object.values(parsed).forEach(rule => {
        if (typeof rule.trigger_handles === 'string') {
          try {
            rule.trigger_handles = JSON.parse(rule.trigger_handles);
          } catch (e) {
            Utils.error('Failed to parse trigger_handles', e);
          }
        }
        // Guard against a single scalar handle (string) surviving JSON.parse (e.g. the
        // stored value was `"handle-a"` instead of `["handle-a"]`). Without this, `.some()`
        // in checkRuleMatch throws, gets swallowed by the per-rule try/catch, and the rule
        // is silently skipped forever even though it visually "matches" in admin.
        rule.trigger_handles = normalizeGiftHandles(rule.trigger_handles);
      });
      
      return parsed;
    }

    setupEventListeners() {
      document.addEventListener('cart:updated', this._onCartUpdate);
      document.addEventListener('cart:refresh', this._onCartUpdate);
      document.addEventListener('submit', this._onFormSubmit, true);

      window.addEventListener('pageshow', this.handlePageShow);
      this.interceptCheckoutNavigation();
      
      window.addEventListener('gift-rules-ready', () => {
        this.loadGiftRules();
      });
      
      this.interceptCartRemovals();
      this.interceptCartChangeNavigation();
      this.subscribeToStorefrontCartPubSub();
      this.interceptCartApiCalls();
      this.warmCart();
    }

    /**
     * One idle read of the cart at startup.
     *
     * The add interceptor has to answer "would this add cross the threshold?"
     * synchronously enough to rewrite a request that is already on its way, which
     * it can only do against a cart it already holds. Without this warm-up the
     * very first add of a session — the common case — always misses, because
     * nothing had read the cart yet.
     */
    warmCart() {
      if (lastKnownCart) return;
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 300));
      idle(() => {
        CartService.getCart().catch(() => {});
      });
    }

    subscribeToStorefrontCartPubSub() {
      try {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          if (window._giftExtensionFetching) return;
          const mgrEarly = window.giftManager?.instance();
          if (!mgrEarly || mgrEarly.isProcessing) return;
          setTimeout(() => {
            const mgr = window.giftManager?.instance();
            if (!mgr || mgr.isProcessing || window._giftExtensionFetching) return;
            mgr.checkAndApplyGifts();
          }, CONFIG.DELAYS.POST_CART_ADD);
        });
      } catch (e) {
        Utils.warn('Gift extension: cart-update pubsub hook failed:', e);
      }
    }

    handlePageShow = async (event) => {
      if (event.persisted || this.hasReturnedFromCheckout()) {
        Utils.log('Page show (bfcache or return from checkout) – refreshing cart and re-evaluating gifts');
        await this.checkAndApplyGifts();
      }
    };

    interceptCheckoutNavigation() {
      const STORAGE_KEY = 'gift:wentToCheckout';
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!form?.action) return;
        const isCheckout = form.querySelector('[name="checkout"]') || form.action.includes('/checkout');
        if (isCheckout) {
          try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
        }
      }, true);
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href*="/checkout"]');
        if (link) {
          try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
        }
      }, true);
    }

    hasReturnedFromCheckout() {
      try {
        const wentToCheckout = sessionStorage.getItem('gift:wentToCheckout');
        if (wentToCheckout) return true;
        const referrer = document.referrer || '';
        return /checkout|cart\.shopify\.com/i.test(referrer);
      } catch {
        return false;
      }
    }
    
    interceptCartApiCalls() {
      if (window.fetch._giftExtensionWrapped) return;
      
      const originalFetch = window.fetch;
      
      window.fetch = async function(...args) {
        const [url, options] = args;
        const urlString = typeof url === 'string' ? url : (url instanceof Request ? url.url : '');
        const method = String(
          (url instanceof Request ? url.method : options?.method) || 'GET'
        ).toUpperCase();
        const isCartMutationPost = isShopifyCartMutationPost(urlString, method);

        const combined = await maybeAddGiftInSameRequest({
          urlString,
          method,
          options,
          isRequestObject: url instanceof Request,
          isInternal: window._giftExtensionFetching,
          originalFetch,
          context: this,
          args
        });

        const response = combined || await originalFetch.apply(this, args);

        const isInternalCall = window._giftExtensionFetching;
        if (isCartMutationPost && response.ok && !isInternalCall) {
          // Mark the cart unverified the instant somebody else mutates it — the
          // theme's own quantity controls post here and the re-check below is
          // half a second behind them. That half second is the window a shopper
          // can carry a stranded gift into checkout through.
          CheckoutGuard.setPending(true);
          setTimeout(async () => {
            try {
              if (window.giftManager?.instance()) {
                await window.giftManager.instance().checkAndApplyGifts();
              }
            } finally {
              CheckoutGuard.setPending(false);
            }
          }, CONFIG.DELAYS.POST_CART_ADD);
        }
        
        return response;
      };
      
      window.fetch._giftExtensionWrapped = true;
    }

    async handleCartUpdate(event) {
      if (this.isProcessing) return;

      try {
        const cart = event.detail?.cart;
        
        if (CartStateTracker.isIncomplete(cart) || !cart) {
          setTimeout(async () => {
            await this.checkAndApplyGifts();
          }, 300);
        } else {
          await this.checkAndApplyGifts();
        }
      } catch (error) {
        Utils.error('Error handling cart update:', error);
      }
    }

    async handleFormSubmit(event) {
      try {
        const form = event.target;
        if (!form) return;

        // Recorded before any early return below: the POST this submit is about to
        // produce carries a variant id and no handle, and without a handle there is
        // no price, and without a price no way to know whether this is the add that
        // earns the gift. See PendingAdd.
        const variantField = form.querySelector('[name="id"]');
        if (variantField?.value) {
          PendingAdd.note({
            variantId: variantField.value,
            handle: PendingAdd.handleFromForm(form),
            sellingPlan: form.querySelector('[name="selling_plan"]')?.value || null
          });
        }

        const hasGiftTrigger = form.querySelector('[data-gift-trigger]');
        if (hasGiftTrigger) return;

        const actionMatches = form.action?.includes('/cart/add');

        const isCartAddForm = actionMatches ||
          (form.method?.toLowerCase() === 'post' && !!form.querySelector('input[name="id"]'));

        if (isCartAddForm) {
          setTimeout(async () => {
            await this.checkAndApplyGifts();
          }, CONFIG.DELAYS.FORM_SUBMIT);
        }
      } catch (error) {
        Utils.error('Error handling form submit:', error);
      }
    }

    interceptCartRemovals() {
      document.addEventListener('click', this.handleCartClick, true);
    }

    interceptCartChangeNavigation() {
      window.addEventListener('beforeunload', (event) => {
        if (window.location.href.includes('/cart/change') && window.location.href.includes('quantity=0')) {
          event.preventDefault();
          event.returnValue = '';
          return '';
        }
      });

      const originalPushState = history.pushState;
      const originalReplaceState = history.replaceState;
      
      history.pushState = function(...args) {
        const url = args[2];
        if (url?.includes('/cart/change') && url.includes('quantity=0')) {
          return;
        }
        return originalPushState.apply(this, args);
      };
      
      history.replaceState = function(...args) {
        const url = args[2];
        if (url?.includes('/cart/change') && url.includes('quantity=0')) {
          return;
        }
        return originalReplaceState.apply(this, args);
      };
    }

    /**
     * The cart drawer does not use `/cart/change` links at all — its remove control
     * is a <cart-remove-button> custom element that calls the theme's own
     * updateQuantity(). So the atomic removal below only ever applied to the /cart
     * page, while the drawer (cart_type = drawer, the surface nearly every shopper
     * actually uses) went the slow way: remove the trigger now, drop the stranded
     * gift a beat later.
     *
     * Deliberately narrow. It takes over only when there is a gift that this
     * removal could actually strand, and never for bundle lines — cart-drawer.js
     * and bfg-bundle-guard.js have their own removal machinery for those, with
     * quantity propagation this knows nothing about. Everything it declines falls
     * through to the theme untouched, where the checkout guard still covers it.
     *
     * The decision has to be synchronous: the theme's own handler is one click
     * listener away, so there is no room to fetch the cart first. It is made
     * against the last cart read, and re-checked against a fresh one inside.
     */
    shouldInterceptDrawerRemove(removeHost) {
      const lineIndex = parseInt(removeHost?.dataset?.index || '', 10);
      if (!Number.isFinite(lineIndex) || lineIndex < 1) return false;

      const items = lastKnownCart?.items;
      if (!Array.isArray(items) || items.length === 0) return false;
      if (!items.some(isGiftItem)) return false;

      const target = items[lineIndex - 1];
      if (!target) return false;
      if (isAnyGiftItem(target)) return false;

      const properties = target.properties || {};
      if (properties._bundle_id || properties._lp_source) return false;

      return true;
    }

    handleDrawerRemoveClick(event, removeHost) {
      if (!this.shouldInterceptDrawerRemove(removeHost)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const lineIndex = parseInt(removeHost.dataset.index, 10);
      this.toggleLineItemLoading(removeHost, true);

      (async () => {
        try {
          const cart = await CartService.getCart();
          const itemToRemove = cart?.items?.[lineIndex - 1];

          // Re-checked against the fresh cart: the drawer may have re-rendered
          // between the cached read and this click, moving the line.
          if (!itemToRemove || isAnyGiftItem(itemToRemove)) {
            throw new Error(`Line ${lineIndex} is no longer a removable trigger`);
          }

          const result = await this.removeItemWithConnectedGifts(itemToRemove, cart.items);
          if (!result) throw new Error('Removal operation failed');

          await this.updateCartUI(result);
        } catch (error) {
          Utils.error('Drawer removal intercept failed, handing back to the theme:', error);
          await this.checkAndApplyGifts();
        } finally {
          this.toggleLineItemLoading(removeHost, false);
        }
      })();
    }

    handleCartClick = async (event) => {
      const link = event.target.closest('a[href*="/cart/change"]');
      if (!link) {
        const removeHost = event.target.closest?.('cart-remove-button');
        if (removeHost) this.handleDrawerRemoveClick(event, removeHost);
        return;
      }
      if (link.hasAttribute('data-gift-manager-processing')) {
        const startedAt = parseInt(link.getAttribute('data-gift-manager-processing-at') || '0', 10);
        const isStale = !startedAt || (Date.now() - startedAt > 8000);
        if (!isStale) return;

        // Self-heal stale locked links to avoid permanent "Removing..." state.
        this.restorePersistedLinkState(link);
      }
      
      const url = new URL(link.href);
      const quantity = url.searchParams.get('quantity');
      
      if (quantity !== '0') return;
      
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      const linkState = this.saveLinkState(link);
      this.showLoadingState(link);
      
      try {
        const itemId = url.searchParams.get('id');
        const lineParam = url.searchParams.get('line');
        
        const cart = await CartService.getCart();
        if (!cart?.items) {
          throw new Error('Could not fetch cart');
        }
        
        let itemToRemove = null;

        // Some themes use ?id=<line_item_key>, others use ?line=<index>.
        if (itemId) {
          itemToRemove = cart.items.find(item => item.key === itemId) || null;
        }

        if (!itemToRemove && lineParam) {
          const lineIndex = parseInt(lineParam, 10) - 1;
          if (!isNaN(lineIndex) && lineIndex >= 0 && lineIndex < cart.items.length) {
            itemToRemove = cart.items[lineIndex];
          }
        }

        // Fallback for drawer markup like id="CartDrawer-Item-2".
        if (!itemToRemove) {
          const rowId = link.closest('tr')?.id || '';
          const rowMatch = rowId.match(/CartDrawer-Item-(\d+)/);
          if (rowMatch) {
            const drawerIndex = parseInt(rowMatch[1], 10) - 1;
            if (!isNaN(drawerIndex) && drawerIndex >= 0 && drawerIndex < cart.items.length) {
              itemToRemove = cart.items[drawerIndex];
            }
          }
        }

        if (!itemToRemove) {
          throw new Error(`Item not found in cart (id=${itemId || 'n/a'}, line=${lineParam || 'n/a'})`);
        }
        
        const result = await this.removeItemWithConnectedGifts(itemToRemove, cart.items);
        
        if (result) {
          this.restoreLinkState(link, linkState);

          // Keep this flow aligned with normal quantity-based cart updates by
          // using updateCartUI() (which delegates to NorseCartUI.refreshCartDrawer)
          // instead of doing a separate manual refresh routine.
          const remaining = Array.isArray(result?.items) ? result.items : [];
          const nonGiftRemaining = remaining.filter((i) => !isAnyGiftItem(i));
          const discountGiftsRemaining = remaining.filter(isDiscountCodeGiftItem);

          if (nonGiftRemaining.length === 0 && discountGiftsRemaining.length > 0) {
            const cleanupUpdates = discountGiftsRemaining.reduce((acc, gift) => {
              acc[gift.key] = 0;
              return acc;
            }, {});
            const cleanCart = await CartService.updateItems(cleanupUpdates);
            DiscountCodeWidget.clearAppliedCode();
            const latestCart = await CartService.getCart();
            await this.updateCartUI(latestCart || cleanCart || result);
            if (typeof DiscountCodeWidget._render === 'function') {
              DiscountCodeWidget._render({ clearInput: true });
            }
          } else if (remaining.length === 0) {
            DiscountCodeWidget.clearAppliedCode();
            const latestCart = await CartService.getCart();
            await this.updateCartUI(latestCart || result);
            if (typeof DiscountCodeWidget._render === 'function') {
              DiscountCodeWidget._render({ clearInput: true });
            }
          } else {
            await this.updateCartUI(result);
          }
        } else {
          throw new Error('Removal operation failed');
        }
      } catch (error) {
        Utils.error('Error intercepting cart removal:', error);
        this.restoreLinkState(link, linkState);
        setTimeout(() => {
          window.location.href = '/cart';
        }, 100);
      }
    };

    saveLinkState(link) {
      return {
        href: link.href,
        onclick: link.onclick,
        innerHTML: link.innerHTML,
        processing: link.hasAttribute('data-gift-manager-processing')
      };
    }

    toggleLineItemLoading(link, isLoading) {
      if (!link) return;
      const fn = window.NorseCartUI && window.NorseCartUI.setRemoveControlLoading;
      if (typeof fn === 'function') {
        fn(link, isLoading);
      }
    }

    showLoadingState(link) {
      if (!link) return;
      if (!link.getAttribute('data-gift-orig-href')) {
        link.setAttribute('data-gift-orig-href', link.getAttribute('href') || '');
      }
      if (!link.getAttribute('data-gift-orig-html')) {
        link.setAttribute('data-gift-orig-html', link.innerHTML || '');
      }

      link.setAttribute('data-gift-manager-processing', 'true');
      link.setAttribute('data-gift-manager-processing-at', String(Date.now()));
      link.href = 'javascript:void(0);';
      link.onclick = (e) => e.preventDefault();
      link.style.pointerEvents = 'none';
      this.toggleLineItemLoading(link, true);

      // Keep remove control visually disabled too.
      link.style.opacity = '0.5';
    }

    restorePersistedLinkState(link) {
      if (!link) return;

      const originalHref = link.getAttribute('data-gift-orig-href');
      const originalHtml = link.getAttribute('data-gift-orig-html');

      if (originalHref !== null) {
        link.setAttribute('href', originalHref);
      }
      if (originalHtml !== null) {
        link.innerHTML = originalHtml;
      }
      if (link.style) link.style.pointerEvents = '';
      if (link.style) link.style.opacity = '';
      if (link.removeAttribute) {
        link.removeAttribute('data-gift-manager-processing');
        link.removeAttribute('data-gift-manager-processing-at');
        link.removeAttribute('data-gift-orig-href');
        link.removeAttribute('data-gift-orig-html');
      }
      link.onclick = null;
      this.toggleLineItemLoading(link, false);
    }

    restoreLinkState(link, state) {
      if (!link || !state) return;
      
      if (link.removeAttribute) {
        link.removeAttribute('data-gift-manager-processing');
        link.removeAttribute('data-gift-manager-processing-at');
        link.removeAttribute('data-gift-orig-href');
        link.removeAttribute('data-gift-orig-html');
      }
      if (state.href) link.href = state.href;
      if (state.onclick !== undefined) link.onclick = state.onclick;
      if (link.style) link.style.pointerEvents = '';
      if (link.style) link.style.opacity = '';
      if (state.innerHTML) link.innerHTML = state.innerHTML;
      this.toggleLineItemLoading(link, false);
    }

    async removeItemWithConnectedGifts(itemToRemove, cartItems) {
      try {
        const removedProductHandle = Utils.getProductHandleFromUrl(itemToRemove.url);
        let connectedGifts = [];

        if (!removedProductHandle) {
          // Non-product line items (or unusual URLs) should still be removable.
          Utils.warn('Could not get product handle from URL, removing line item only:', itemToRemove.url);
        } else {
          connectedGifts = GiftRulesManager.findConnectedGifts(
            removedProductHandle,
            cartItems,
            itemToRemove,
            this.giftToTriggerMapping
          );
        }
        
        const strandedGifts = await this.findGiftsStrandedByRemoval(cartItems, itemToRemove);

        const updates = {
          [itemToRemove.key]: 0,
          ...[...connectedGifts, ...strandedGifts].reduce((acc, gift) => {
            if (gift?.key) acc[gift.key] = 0;
            return acc;
          }, {})
        };

        const updatedCart = await CartService.updateItems(updates);
        
        if (updatedCart) {
          await enrichCartWithSections(updatedCart);
          requestAnimationFrame(() => {
            EventDispatcher.dispatchCartEventsSmooth(updatedCart);
          });
          return updatedCart;
        }
        
        return null;
      } catch (error) {
        Utils.error('Error removing item with connected gifts:', error);
        throw error;
      }
    }

    /**
     * Which gifts the cart would no longer earn once `itemToRemove` is gone.
     *
     * findConnectedGifts() only knows gifts wired to a trigger product, because it
     * walks the gift→trigger map and a "Price" rule contributes nothing to that map
     * (buildGiftToTriggerMapping skips rules with no trigger_handles). Threshold
     * promos are exactly the ones at risk: drop the line that carried the cart over
     * $100 and the gift is left behind, free and checkout-ready, until the async
     * re-check catches up.
     *
     * Running the real evaluator against the projected cart closes that, and closes
     * it the same way for every rule type. The result is merged into the caller's
     * single /cart/update.js, so the trigger and its gift leave together in one
     * request and there is no in-between state to click through.
     *
     * Removals only. A lower threshold becoming the best match is a gift to ADD,
     * which needs /cart/add.js and cannot ride along in the same call — the normal
     * re-check picks it up a moment later, and a gift arriving late is nobody's
     * exploit.
     */
    async findGiftsStrandedByRemoval(cartItems, itemToRemove) {
      if (!this.evaluator) return [];

      try {
        const projected = (cartItems || []).filter((item) => item?.key !== itemToRemove?.key);

        // Nothing paid left: every gift goes, whatever the rules say. Covers
        // _discountCodeGift lines too, which the evaluator does not consider.
        if (!projected.some((item) => !isAnyGiftItem(item))) {
          return projected.filter(isAnyGiftItem);
        }

        const evaluation = await this.evaluator.evaluate(projected);
        return (evaluation?.shouldRemove || []).filter((gift) => gift?.updateQuantity !== 1);
      } catch (error) {
        // The line the shopper asked to remove must still go. The async re-check
        // is the fallback for the gift, and the checkout guard covers the gap.
        Utils.warn('Could not project gifts for removal, removing the line only:', error);
        return [];
      }
    }

    async updateCartUI(cart) {
      const ready = await enrichCartWithSections(cart);
      await refreshCartDrawerDom();

      requestAnimationFrame(() => {
        EventDispatcher.dispatchCartEventsSmooth(ready || cart);
      });
    }

    async checkAndApplyGifts() {
      return this.debouncer.debounce(async () => {
        await this.runEvaluation();
      })();
    }

    /**
     * The single door into processCartUpdate(), so concurrent callers queue behind
     * each other instead of silently dropping.
     *
     * processCartUpdate() returns immediately when one is already running — right
     * for a background re-check, wrong for anything awaiting a verdict: the caller
     * gets a resolved promise having done nothing, and then reads a cart the run
     * it collided with is still halfway through changing.
     */
    async runEvaluation() {
      if (this._evaluation) {
        try { await this._evaluation; } catch (_) { /* logged by its own caller */ }
      }

      this._evaluation = this.processCartUpdate();
      try {
        await this._evaluation;
      } finally {
        this._evaluation = null;
      }
    }

    /**
     * Same evaluation as checkAndApplyGifts(), minus the wait: the debounce is
     * dropped. The checkout guard calls this with the shopper's click held open,
     * so the 150 ms debounce — right for a background re-check — would only add
     * latency to a decision the shopper is standing in front of.
     */
    async forceEvaluate() {
      this.debouncer.cancel();
      await this.runEvaluation();
    }

    async processCartUpdate() {
      if (this.isProcessing) return;
      if (!this.giftRules || Object.keys(this.giftRules).length === 0) return;
      if (!this.evaluator) return;

      this.isProcessing = true;

      try {
        // Always read current cart from the API so price/trigger evaluation matches checkout
        // (event payloads can be stale or carry totals that include gift lines incorrectly).
        let cartData = await CartService.getCart();
        if (!cartData) return;

        // Self-heal discount-code gift lines on every cart mutation: exactly one line,
        // quantity 1. Covers every path that can inflate the free gift (quantity "+" in
        // the drawer, /cart page inputs, re-applying the code from another tab/session).
        const discountGiftUpdates = this.buildDiscountGiftNormalizationUpdates(cartData.items || []);
        if (Object.keys(discountGiftUpdates).length > 0) {
          Utils.log('Normalizing discount-code gift lines:', discountGiftUpdates);
          await CartService.updateItems(discountGiftUpdates);
          cartData = await CartService.getCart();
          if (!cartData) return;
        }

        const cartItems = cartData.items || [];
        
        if (cartItems.length === 0) {
          await this.removeAllGifts();
          return;
        }

        if (this.hasReturnedFromCheckout()) {
          try { sessionStorage.removeItem('gift:wentToCheckout'); } catch (_) {}
          if (cartItems.some(isGiftItem)) {
            await this.removeAllGifts();
            return;
          }
        }
        
        const evaluation = await this.evaluator.evaluate(cartItems);
        
        if (evaluation.shouldAdd.length > 0 || evaluation.shouldRemove.length > 0) {
          await this.updateCartItems(evaluation);
        } else {
          const cartSignature = CartStateTracker.createSignature(cartData);
          if (cartSignature !== this.lastCartState) {
            this.lastCartState = cartSignature;
            await enrichCartWithSections(cartData);
            EventDispatcher.dispatchCartEventsSmooth(cartData);
          }
        }
      } catch (error) {
        Utils.error('Error checking and applying gifts:', error);
      } finally {
        this.isProcessing = false;
      }
    }

    /**
     * A discount-code promo grants exactly one free gift: keep the first
     * _discountCodeGift line clamped to quantity 1 and remove any additional
     * discount-gift lines (duplicates or leftovers from older promos).
     */
    buildDiscountGiftNormalizationUpdates(cartItems) {
      const updates = {};
      let keptOne = false;

      (cartItems || []).forEach((item) => {
        if (!isDiscountCodeGiftItem(item) || !item?.key) return;
        if (!keptOne) {
          keptOne = true;
          if (item.quantity > 1) updates[item.key] = 1;
        } else {
          updates[item.key] = 0;  
        }
      });

      return updates;
    }

    async removeAllGifts() {
      const currentCart = await CartService.getCart();
      if (!currentCart?.items?.length) return;
      
      const giftsToRemove = currentCart.items.filter(isGiftItem);
      if (giftsToRemove.length === 0) return;
      
      const updates = giftsToRemove.reduce((acc, gift) => {
        acc[gift.key] = 0;
        return acc;
      }, {});
      
      const finalCart = await CartService.updateItems(updates);
      if (finalCart) {
        await enrichCartWithSections(finalCart);
        EventDispatcher.dispatchCartEventsSmooth(finalCart);
      }
    }

    async updateCartItems(evaluation) {
      try {
        const updates = {};
        let hasRemovals = false;

        for (const gift of evaluation.shouldRemove) {
          if (gift.updateQuantity === 1 && gift.key) {
            updates[gift.key] = 1;
          } else if (gift.key) {
            updates[gift.key] = 0;
          }
          hasRemovals = true;
        }

        if (hasRemovals && Object.keys(updates).length > 0) {
          await CartService.updateItems(updates);
        }

        let lastResponse = null;
        if (evaluation.shouldAdd.length > 0) {
          const items = evaluation.shouldAdd.map(gift => ({
            id: gift.variantId,
            quantity: 1,
            properties: { [CONFIG.GIFT_PROPERTY]: 'true' }
          }));

          try {
            lastResponse = await CartService.addMultipleItems(items);
          } catch (addError) {
            Utils.error('Failed to add gifts to cart:', addError);
            throw addError;
          }
        }

        const finalCart = await CartService.getCartWithSections();
        if (!finalCart) return null;

        await enrichCartWithSections(finalCart, lastResponse);

        const cartSignature = CartStateTracker.createSignature(finalCart);
        if (cartSignature !== this.lastCartState) {
          this.lastCartState = cartSignature;
          requestAnimationFrame(() => {
            EventDispatcher.dispatchCartEventsSmooth(finalCart);
          });
          await repaintCartDrawer();
        }

        return finalCart;
      } catch (error) {
        Utils.error('Error updating cart items:', error);
        return null;
      }
    }

    async addGiftToCart(gift) {
      try {
        const response = await CartService.addItem(
          gift.variantId,
          gift.quantity,
          { [CONFIG.GIFT_PROPERTY]: 'true' }
        );
        
        EventDispatcher.dispatchGiftEvent('GIFT_ADDED', { gift, cart: response });
        return response;
      } catch (error) {
        Utils.warn('Error adding gift to cart:', error);
        return null;
      }
    }

    async removeGiftFromCart(gift) {
      try {
        const response = await CartService.updateItems({
          [gift.key]: 0
        });
        
        EventDispatcher.dispatchGiftEvent('GIFT_REMOVED', { gift, cart: response });
        return response;
      } catch (error) {
        Utils.warn('Error removing gift from cart:', error);
        return null;
      }
    }

    async getApplicableGifts(productHandle, purchaseType = 'onetime') {
      if (!this.giftRules || !productHandle) return [];

      const sortedRules = this.evaluator
        ? this.evaluator.sortRulesByPriority(Object.values(this.giftRules), 0)
        : Object.values(this.giftRules);

      for (const rule of sortedRules) {
        if (!rule?.trigger_handles?.length) continue;
        if (!isRuleDateActive(rule)) continue;
        if (!this.matchesPurchaseType(rule.purchase_type, purchaseType)) continue;
        if (!rule.trigger_handles.includes(productHandle)) continue;

        for (const giftHandle of normalizeGiftHandles(rule.gifts)) {
          if (!giftHandle) continue;

          try {
            const variantId = await ProductService.getVariantId(giftHandle);
            if (variantId) {
              return [{ handle: giftHandle, variantId, quantity: 1 }];
            }
          } catch (error) {
            Utils.error('Error getting variant ID for gift:', giftHandle, error);
          }
        }
      }

      return [];
    }

    async addProductWithGifts(productData, giftVariantIds = []) {
      try {
        const uniqueGiftVariantIds = [...new Set(giftVariantIds)].slice(0, 1);
        const items = [
          {
            id: productData.id,
            quantity: productData.quantity || 1,
            ...(productData.selling_plan && { selling_plan: productData.selling_plan })
          },
          ...uniqueGiftVariantIds.map(giftVariantId => ({
            id: giftVariantId,
            quantity: 1,
            properties: { [CONFIG.GIFT_PROPERTY]: 'true' }
          }))
        ];
        
        const addResponse = await CartService.addMultipleItems(items);
        const fullCart = await CartService.getCart();

        if (fullCart) {
          await enrichCartWithSections(fullCart, addResponse);
          EventDispatcher.dispatchCartEvents(fullCart);
        }

        return fullCart;
      } catch (error) {
        Utils.error('Error adding product with gifts:', error);
        throw error;
      }
    }
  }

  window.giftAddToCart = async function(button, options = {}) {
    const REQUEST_LOCK_ATTR = 'data-gift-request-in-flight';

    if (button?.getAttribute(REQUEST_LOCK_ATTR) === 'true') {
      return null;
    }

    if (button?.setAttribute) {
      button.setAttribute(REQUEST_LOCK_ATTR, 'true');
    }

    try {
      const form = button.closest('form');
      if (!form) {
        Utils.warn('No form found for button — falling back to normal add');
        return null;
      }

      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = formData.get('quantity') || 1;
      const purchaseTypeValue = formData.get('purchase_type');
      const sellingPlan = formData.get('selling_plan');

      if (!variantId) {
        Utils.warn('No variant ID found in form — falling back to normal add');
        return null;
      }

      const purchaseType = purchaseTypeValue === 'subscription' ? 'subscription' : 'onetime';
      const shouldIncludeSellingPlan = purchaseType === 'subscription' && sellingPlan;
      const cartItemData = {
        id: variantId,
        quantity,
        ...(shouldIncludeSellingPlan && { selling_plan: sellingPlan })
      };

      const productHandle = button.getAttribute('data-gift-trigger');
      const manager = window.giftManager?.instance?.();

      let applicableGifts = [];
      if (productHandle && manager) {
        try {
          applicableGifts = await manager.getApplicableGifts(productHandle, purchaseType);
        } catch (giftErr) {
          Utils.warn('Error finding applicable gifts:', giftErr);
        }
      }

      if (applicableGifts.length > 0) {
        const giftVariantIds = applicableGifts.map(gift => gift.variantId);
        return await manager.addProductWithGifts(cartItemData, giftVariantIds);
      }

      const addResponse = await CartService.addSingleItem(cartItemData);
      const fullCart = await CartService.getCart();
      if (fullCart) {
        await enrichCartWithSections(fullCart, addResponse);
      }
      return fullCart;
    } catch (error) {
      Utils.error('Error in giftAddToCart:', error);
      return null;
    } finally {
      if (button?.removeAttribute) {
        button.removeAttribute(REQUEST_LOCK_ATTR);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // Discount Code Widget
  // Handles "Discount Code" gift rules: shows a promo-code field above the cart
  // total, adds the associated gift product on Apply, and pre-applies the code
  // at Shopify checkout.
  // ---------------------------------------------------------------------------
  // How long a message holds the line before it falls back to its resting state,
  // where it lists the codes currently applied (or shows nothing). Errors get
  // longer because they carry something the shopper has to read.
  const DISCOUNT_MESSAGE_RESET_MS = 1000;
  const DISCOUNT_ERROR_RESET_MS = 3000;

  const DiscountCodeWidget = {
    discountRules: [],
    appliedGiftCodes: [],
    appliedStoreCodes: [],
    STORAGE_KEY: 'gift:discountCode',
    STORE_STORAGE_KEY: 'gift:storeDiscountCodes',
    _listenersBound: false,
    _busy: false,
    _normalizing: false,
    _msgTimer: null,
    _msgText: '',
    _msgType: '',
    _applyText: '',

    // Idempotent: safe to call again when GIFT_RULES arrives after first init
    // (listeners are bound once, rules are refreshed every time).
    init(giftRules) {
      // The rule name is the key in GIFT_RULES, so it is carried onto the rule to
      // name the offending entry in the misconfiguration warning below.
      const allRules = Object.entries(giftRules || {}).map(([name, rule]) => {
        const normalized = this._normalizeRule(rule);
        normalized._ruleName = name;
        return normalized;
      });

      // Any rule carrying a code can be claimed with that code, whatever its
      // "Gift rule based on" says: the dropdown is easy to leave on "Product"
      // while filling the code field in, and a code that exists is meant to work.
      // The rule's own conditions — date window, minimum order, trigger products —
      // are still enforced when it is applied.
      this.discountRules = allRules.filter(rule => rule?.discount_code);

      // Intentionally no early return on an empty rule set: the field also takes
      // plain Shopify discount codes, which exist independently of gift rules.
      this.appliedGiftCodes = this._loadGiftCodes();
      this.appliedStoreCodes = this._loadStoreCodes();
      this._injectStyles();
      if (!this._listenersBound) {
        this._setupDelegation();
        this._setupCartListeners();
        this._listenersBound = true;
      }
      this._showWidget();
      setTimeout(() => this._syncWithCartState(), 100);
    },

    _loadGiftCodes() {
      try {
        const raw = sessionStorage.getItem(this.STORAGE_KEY);
        if (!raw) return [];
        // Older sessions stored a single bare code under this key.
        try {
          const parsed = JSON.parse(raw);
          return this._uniqueCodes(Array.isArray(parsed) ? parsed : [parsed]);
        } catch {
          return this._uniqueCodes([raw]);
        }
      } catch { return []; }
    },
    _saveGiftCodes(codes) {
      try {
        if (!codes || codes.length === 0) sessionStorage.removeItem(this.STORAGE_KEY);
        else sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(codes));
      } catch {}
    },

    clearAppliedCode() {
      this.appliedGiftCodes = [];
      this._saveGiftCodes([]);
    },

    _loadStoreCodes() {
      try {
        const raw = sessionStorage.getItem(this.STORE_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? this._uniqueCodes(parsed) : [];
      } catch { return []; }
    },
    _saveStoreCodes(codes) {
      try {
        if (!codes || codes.length === 0) sessionStorage.removeItem(this.STORE_STORAGE_KEY);
        else sessionStorage.setItem(this.STORE_STORAGE_KEY, JSON.stringify(codes));
      } catch {}
    },

    _uniqueCodes(codes) {
      const list = [];
      (codes || []).forEach((raw) => {
        const code = String(raw || '').trim();
        if (!code) return;
        if (!list.some(existing => existing.toUpperCase() === code.toUpperCase())) list.push(code);
      });
      return list;
    },

    // Every code currently in force: gift-rule codes plus plain Shopify codes.
    _activeCodes() {
      return this._uniqueCodes([...this.appliedGiftCodes, ...this.appliedStoreCodes]);
    },


    _setupDelegation() {
      document.addEventListener('click', (e) => {
        if (e.target.closest('#gift-discount-code-apply')) {
          this._handleApply();
          return;
        }
        // No checkout interception: the cart session carries every applied code
        // into checkout on its own, and a `?discount=` param can only name one.
      });

      document.addEventListener('keydown', (e) => {
        if (e.target.id === 'gift-discount-code-input' && e.key === 'Enter') {
          e.preventDefault();
          this._handleApply();
        }
      });
    },

    _setupCartListeners() {
      // After any cart re-render (from any source) re-show the widget and restore state.
      // The 500ms delay covers the async fetch+replaceWith inside CartDrawerItems.onCartUpdate().
      let _renderTimer = null;
      const onRender = () => {
        clearTimeout(_renderTimer);
        _renderTimer = setTimeout(async () => {
          this._showWidget();
          await this._syncWithCartState();
        }, 500);
      };

      document.addEventListener('cart:updated', onRender);
      document.addEventListener('cart:refresh', onRender);
      document.documentElement.addEventListener('cart:updated', onRender);
      document.documentElement.addEventListener('cart:refresh', onRender);
    },

    _showWidget() {
      const widget = document.getElementById('gift-discount-code-widget');
      if (!widget) return;

      // Always shown: the field takes plain Shopify discount codes, which have
      // nothing to do with the gift promos, so hiding it between them would leave a
      // shopper holding a valid code with nowhere to enter it.
      widget.removeAttribute('hidden');
      widget.style.display = '';
    },

    // Single render path for the widget. Applied codes are reported on the message
    // line, so the field itself always returns to its placeholder and another code
    // can be stacked on top of the active ones.
    _render({ busy = this._busy, clearInput = false } = {}) {
      this._paintMsg();

      const input = document.getElementById('gift-discount-code-input');
      const btn   = document.getElementById('gift-discount-code-apply');
      if (input) {
        input.disabled = busy;
        if (clearInput) input.value = '';
      }
      if (btn) {
        // The label is localised in Liquid, so it is read from the DOM rather than
        // written in English here; the ellipsis reads the same in every language.
        if (!busy && !this._applyText) this._applyText = btn.textContent.trim();
        const label = this._applyText || 'Apply';
        btn.disabled = busy;
        btn.textContent = busy ? `${label}\u2026` : label;
      }
    },

    // Resting text for the message line: what is applied right now.
    _appliedSummary() {
      const codes = this._activeCodes();
      if (codes.length === 0) return '';
      const quoted = codes.map(code => `"${code}"`).join(', ');
      return codes.length > 1 ? `Codes ${quoted} applied!` : `Code ${quoted} applied!`;
    },

    // The message line keeps reporting the applied codes, so the confirmation only
    // needs to hand the field back in its default state.
    _flashSuccess(text) {
      this._showMsg(text, 'success');

      const input = document.getElementById('gift-discount-code-input');
      const flashed = input?.value || '';

      clearTimeout(this._msgTimer);
      this._msgTimer = setTimeout(() => {
        this._showMsg('', '');
        const current = document.getElementById('gift-discount-code-input');
        // Anything the shopper has started typing since the flash is left alone.
        this._render({ busy: false, clearInput: !current || current.value === flashed });
      }, DISCOUNT_MESSAGE_RESET_MS);
    },

    // Errors clear themselves as well, handing the line back to whatever is
    // actually applied — or to nothing when no code is.
    _flashError(text) {
      this._showMsg(text, 'error');
      clearTimeout(this._msgTimer);
      this._msgTimer = setTimeout(() => this._showMsg('', ''), DISCOUNT_ERROR_RESET_MS);
    },


    _hasDiscountGiftInCart(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      return items.some((item) => isDiscountCodeGiftItem(item));
    },

    // Same lookup as _findActiveRuleForCode but ignoring the date window, so an
    // ended promo can be told apart from a code that never existed.
    _findRuleForCode(code) {
      const normalized = String(code || '').trim().toUpperCase();
      if (!normalized) return null;
      return this.discountRules.find(
        (rule) => (rule.discount_code || '').trim().toUpperCase() === normalized
      ) || null;
    },

    // Every rule in its date window that this code claims, highest minimum order
    // first: a Promotion can give several gift tiers for the same code.
    _activeRulesForCode(code) {
      const normalized = String(code || '').trim().toUpperCase();
      if (!normalized) return [];
      return this.discountRules
        .filter(
          (rule) =>
            (rule.discount_code || '').trim().toUpperCase() === normalized &&
            isRuleDateActive(rule)
        )
        .sort((a, b) => (parseFloat(b?.price) || 0) - (parseFloat(a?.price) || 0));
    },

    _findActiveRuleForCode(code) {
      return this._activeRulesForCode(code)[0] || null;
    },

    async _syncWithCartState() {
      try {
        const cart = await CartService.getCart();

        // A cart we could not read tells us nothing. Treating a failed or
        // rate-limited response as an empty cart drops the record of which codes
        // are in force and unlocks the widget for no reason.
        if (!cart) {
          this._render();
          return;
        }

        const items = Array.isArray(cart.items) ? cart.items : [];
        const hasDiscountGift = this._hasDiscountGiftInCart(cart);
        const nonGiftItems = items.filter((item) => !isAnyGiftItem(item));

        // The cart is the source of truth for both kinds of code: the discount
        // session and the gift lines both outlive this tab's sessionStorage, so a
        // second window cannot claim the same gift twice.
        this.appliedStoreCodes = this._extractCartDiscountCodes(cart);
        this._saveStoreCodes(this.appliedStoreCodes);
        this.appliedGiftCodes = this._extractCartGiftCodes(cart);
        this._saveGiftCodes(this.appliedGiftCodes);

        // If only discount-code gifts remain and there are no real products,
        // remove them automatically and reset the widget.
        if (hasDiscountGift && nonGiftItems.length === 0) {
          const discountGifts = items.filter(isDiscountCodeGiftItem);
          if (discountGifts.length > 0) {
            const updates = discountGifts.reduce((acc, gift) => {
              acc[gift.key] = 0;
              return acc;
            }, {});
            await CartService.updateItems(updates);
          }
          this.clearAppliedCode();
          // _refreshCartDrawer re-fetches the cart HTML, updates the DOM, then
          // calls _syncWithCartState again — at that point the cart will be empty
          // and the widget will settle into its ready state cleanly.
          await this._refreshCartDrawer();
          return;
        }

        await this._normalizeGiftLines(items);

        this._render();
      } catch (_) {
        this._render();
      }
    },

    // Gift lines carry the code that produced them, so the cart itself says which
    // gift-rule codes are still in force. sessionStorage is only a hint used before
    // the first successful read — a second window has none of it.
    _extractCartGiftCodes(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const codes = [];

      items.filter(isDiscountCodeGiftItem).forEach((item) => {
        const tagged = String(item?.properties?._discountCode || '').trim();
        if (tagged) {
          codes.push(tagged);
          return;
        }

        // Lines added before the code was tagged: match them back to a rule by the
        // gift product they hold.
        const handle = this._extractHandleFromCartItem(item);
        if (!handle) return;
        this.discountRules.forEach((rule) => {
          const gifts = normalizeGiftHandles(rule.gifts).map(g => String(g || '').trim());
          if (gifts.includes(handle)) codes.push(rule.discount_code);
        });
      });

      return this._uniqueCodes(codes);
    },

    // Defence in depth: a discount gift is one unit. Applying already collapses
    // duplicate lines, but nothing re-checked the quantity afterwards, so the
    // drawer's stepper could still multiply a free gift.
    async _normalizeGiftLines(items) {
      if (this._normalizing) return;

      const updates = {};
      items.filter(isDiscountCodeGiftItem).forEach((item) => {
        if (item?.key && (item.quantity || 1) > 1) updates[item.key] = 1;
      });

      if (Object.keys(updates).length === 0) return;

      this._normalizing = true;
      try {
        await CartService.updateItems(updates);
        await this._refreshCartDrawer();
      } finally {
        this._normalizing = false;
      }
    },


    _normalizeRule(rule) {
      const normalized = { ...(rule || {}) };

      if (typeof normalized.trigger_handles === 'string') {
        try {
          normalized.trigger_handles = JSON.parse(normalized.trigger_handles);
        } catch {
          normalized.trigger_handles = [];
        }
      }
      if (!Array.isArray(normalized.trigger_handles)) {
        normalized.trigger_handles = [];
      }
      normalized.trigger_handles = normalized.trigger_handles
        .map((h) => String(h || '').trim())
        .filter(Boolean);

      const parsedPrice = parseFloat(normalized.price);
      normalized.price = Number.isFinite(parsedPrice) && parsedPrice > 0 ? parsedPrice : null;

      normalized.gift_rule_based_on = String(ruleFieldValue(normalized.gift_rule_based_on)).trim();
      normalized.discount_code = String(ruleFieldValue(normalized.discount_code)).trim();

      return normalized;
    },

    _extractHandleFromCartItem(item) {
      if (!item) return '';
      if (item.handle) return String(item.handle);
      return Utils.getProductHandleFromUrl(item.url || '');
    },

    _buildCartConditionContext(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const nonGiftItems = items.filter((item) => !isAnyGiftItem(item));

      let subtotalInCents = 0;
      const handleToQty = new Map();

      nonGiftItems.forEach((item) => {
        const qty = item.quantity || 1;
        const linePrice = item.final_line_price || item.line_price ||
          ((item.final_price || item.price || 0) * qty);
        subtotalInCents += Number(linePrice) || 0;

        const handle = this._extractHandleFromCartItem(item);
        if (!handle) return;
        handleToQty.set(handle, (handleToQty.get(handle) || 0) + qty);
      });

      return {
        subtotalInDollars: subtotalInCents / 100,
        handleToQty
      };
    },

    // Whether a rule outside its window has not started yet or has ended.
    _dateWindowMessage(rule) {
      const fromDate = rule?.from_date ? new Date(rule.from_date) : null;
      const isNotYetActive = fromDate && !isNaN(fromDate.getTime()) && Date.now() < fromDate.getTime();
      return isNotYetActive ? 'This code is not active yet.' : 'This code has expired.';
    },

    _evaluateRuleConditions(rule, context) {
      const minimumOrder = rule?.price;
      const triggers = Array.isArray(rule?.trigger_handles) ? rule.trigger_handles : [];

      if (!isRuleDateActive(rule)) {
        return { matched: false, message: this._dateWindowMessage(rule) };
      }

      if (minimumOrder !== null && minimumOrder !== undefined) {
        if (context.subtotalInDollars < minimumOrder) {
          return {
            matched: false,
            message: `This code requires a minimum order of $${minimumOrder.toFixed(2)}.`
          };
        }
      }

      if (triggers.length > 0) {
        const hasAnyTrigger = triggers.some((handle) => (context.handleToQty.get(handle) || 0) > 0);
        if (!hasAnyTrigger) {
          return {
            matched: false,
            message: 'This code applies only when required product(s) are in your cart.'
          };
        }
      }

      return { matched: true, message: '' };
    },

    async _handleApply() {
      if (this._busy) return;

      const input = document.getElementById('gift-discount-code-input');
      const code  = (input?.value || '').trim().toUpperCase();

      clearTimeout(this._msgTimer);

      if (!code) {
        this._flashError('Please enter a discount code.');
        return;
      }

      if (this._activeCodes().some(active => active.toUpperCase() === code)) {
        this._flashError('That code is already applied.');
        return;
      }

      // Only a rule inside its date window takes the gift path. An expired promo
      // code falls through and is tried as a plain Shopify code, which it may
      // still be — only the wording of the failure differs.
      const matchedRule = this._findActiveRuleForCode(code);
      const outOfWindowRule = matchedRule ? null : this._findRuleForCode(code);

      this._busy = true;
      this._render();
      this._showMsg('', '');

      try {
        if (matchedRule) await this._applyGiftRuleCode(matchedRule);
        else await this._applyStoreCode(code, {
          // The promo is over (or has not started), but the code may still be a
          // live Shopify discount, so it is tried anyway with a truer message
          // held in reserve.
          failureMessage: outOfWindowRule ? this._dateWindowMessage(outOfWindowRule) : ''
        });
      } catch (err) {
        Utils.error('DiscountCodeWidget: error applying code:', err);
        this._flashError('Something went wrong. Please try again.');
      } finally {
        this._busy = false;
        this._render();
      }
    },

    async _applyGiftRuleCode(rule) {
      const cart = await CartService.getCart();
      if (!cart) {
        throw new Error('Could not load cart');
      }

      const context = this._buildCartConditionContext(cart);
      let conditionCheck = this._evaluateRuleConditions(rule, context);
      if (!conditionCheck.matched) {
        // The highest tier for this code is out of reach: the highest one this
        // cart does meet grants its gift instead. The code box grants one gift.
        const reachable = this._activeRulesForCode(rule.discount_code)
          .find((candidate) => candidate !== rule && this._evaluateRuleConditions(candidate, context).matched);
        if (reachable) {
          rule = reachable;
          conditionCheck = { matched: true, message: '' };
        }
      }
      if (!conditionCheck.matched) {
        this._flashError(conditionCheck.message || 'This code conditions are not met.');
        return;
      }

      // 1. Ensure exactly ONE discount-code gift line (quantity 1) in the cart.
      //    Tagged as _discountCodeGift so the auto-evaluator does not remove it
      //    during its normal gift management cycle, and with the code itself so the
      //    cart can say later which promo produced the line.
      //    Only the first resolvable gift handle is used — a promo grants a single
      //    gift product, never one per configured handle.
      const giftHandles = normalizeGiftHandles(rule.gifts).filter(h => h?.trim());
      let giftVariantId = null;
      for (const handle of giftHandles) {
        try {
          giftVariantId = await ProductService.getVariantId(handle);
          if (giftVariantId) break;
        } catch (variantErr) {
          Utils.warn('DiscountCodeWidget: could not resolve gift handle:', handle, variantErr);
        }
      }

      // The cart may already contain discount-code gifts: re-applies from another
      // tab/session (sessionStorage is per-tab, the cart cookie is not) or leftovers
      // from a previous promo. Keep at most one line of the current promo's gift at
      // quantity 1, and remove everything else so quantities can never stack.
      // A rule with trigger products and a code can be claimed either way now, so
      // the gift may already be in the cart from the automatic path. That line is
      // left alone and counts as the promo's gift, so no second copy is added.
      const autoGiftAlready = (cart.items || []).some(
        item => giftVariantId && item.variant_id === giftVariantId && isGiftItem(item)
      );

      const existingDiscountGifts = (cart.items || []).filter(isDiscountCodeGiftItem);
      const giftLineUpdates = {};
      let hasCurrentGiftAlready = autoGiftAlready;
      existingDiscountGifts.forEach((item) => {
        if (!item?.key) return;
        if (giftVariantId && item.variant_id === giftVariantId && !hasCurrentGiftAlready) {
          hasCurrentGiftAlready = true;
          if (item.quantity > 1) giftLineUpdates[item.key] = 1;
        } else {
          giftLineUpdates[item.key] = 0;
        }
      });
      if (Object.keys(giftLineUpdates).length > 0) {
        await CartService.updateItems(giftLineUpdates);
      }
      if (giftVariantId && !hasCurrentGiftAlready) {
        await CartService.addItem(giftVariantId, 1, {
          '_discountCodeGift': 'true',
          '_discountCode': rule.discount_code
        });
      }

      // 2. Add the code to the Shopify session. Codes already on the cart stay
      //    there, so only this one is sent. Deliberately does not read the cart
      //    back: this sits between adding the gift and refreshing the drawer, so
      //    latency here shows up as the gift appearing late.
      await this._sendCodeToShopify(rule.discount_code, { readCart: false });

      // 3. Persist applied codes.
      this.appliedGiftCodes = this._uniqueCodes([...this.appliedGiftCodes, rule.discount_code]);
      this._saveGiftCodes(this.appliedGiftCodes);

      // 4. Refresh cart drawer using the same fetch-and-replace the theme uses.
      await this._refreshCartDrawer();

      this._flashSuccess(`Code "${rule.discount_code}" applied!`);
    },

    async _applyStoreCode(code, { failureMessage = '' } = {}) {
      const cart = await this._sendCodeToShopify(code);

      // Whatever came back is the truth either way, so the widget adopts it.
      const applied = this._extractCartDiscountCodes(cart);
      this.appliedStoreCodes = applied;
      this._saveStoreCodes(applied);

      const canonical = applied.find(c => c.toUpperCase() === code.toUpperCase());
      if (!canonical) {
        // Shopify recorded the code but marked it not applicable: invalid, expired,
        // already used, not combinable with a code already on the cart, or the cart
        // does not meet its conditions. Nothing to undo — codes already on the
        // session were not touched by this request.
        this._flashError(failureMessage || 'This code is not valid for your cart.');
        return;
      }

      await this._refreshCartDrawer();
      this._flashSuccess(`Code "${canonical}" applied!`);
    },

    // Reads the discount codes Shopify has actually applied to the cart.
    // `cart.discount_codes` is the authoritative list — every code on the session
    // with an `applicable` flag — so it also covers codes whose discount is not
    // visible in the cart itself (free shipping) and excludes ones the cart does
    // not qualify for. Automatic discounts never appear there, which is correct:
    // they are not codes the shopper entered.
    _extractCartDiscountCodes(cart) {
      const flagged = (cart?.discount_codes || [])
        .filter(entry => entry?.applicable)
        .map(entry => entry?.code);

      if (flagged.length > 0) return this._uniqueCodes(flagged);

      // Fallback for responses without `discount_codes`: order discounts land in
      // cart_level_discount_applications, product-level ones only in each line's
      // allocations, so scan both. `type` filtering keeps automatics out.
      const titles = [];
      const collect = (application) => {
        if (!application || application.type !== 'discount_code') return;
        titles.push(application.title || application.code);
      };

      (cart?.cart_level_discount_applications || []).forEach(collect);
      (cart?.discount_applications || []).forEach(collect);
      (Array.isArray(cart?.items) ? cart.items : []).forEach((item) => {
        (item?.line_level_discount_allocations || []).forEach(a => collect(a?.discount_application));
        (item?.discount_allocations || []).forEach(a => collect(a?.discount_application));
      });

      return this._uniqueCodes(titles);
    },

    // Adds ONE code to the cart's discount session. /discount/ takes a single
    // code: a comma-joined list is stored verbatim as one literal code and comes
    // back `applicable: false`, which silently kills every apply made while
    // another code is already active. Codes accumulate on the cart by themselves,
    // so each gets its own request and the ones already applied are left alone.
    async _sendCodeToShopify(code, { readCart = true } = {}) {
      const single = String(code || '').trim();
      if (!single) return readCart ? await CartService.getCart() : null;

      window._giftExtensionFetching = true;
      try {
        // `redirect: 'manual'` stops at the 302 that sets the session cookie —
        // verified to be enough. Following it downloads a whole page first, which
        // is enough to visibly delay the gift being added.
        await fetch(`/discount/${encodeURIComponent(single)}`, {
          redirect: 'manual',
          credentials: 'same-origin'
        });
      } catch (_) {
        /* opaque redirect — the session cookie is still set */
      } finally {
        window._giftExtensionFetching = false;
      }

      return readCart ? await CartService.getCart() : null;
    },


    async _refreshCartDrawer() {
      try {
        // Use the same URL the theme's CartDrawerItems.onCartUpdate() uses.
        const cartUrl =
          (typeof routes !== 'undefined' && routes.cart_url) ||
          window.themeVariables?.routes?.cartUrl ||
          '/cart';

        const response = await fetch(`${cartUrl}?section_id=cart-drawer`);
        if (!response.ok) return;

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        // Replace cart items + the full footer wrapper (subtotal, savings, checkout CTA).
        // Scope to #CartDrawer so we never swap a different `.drawer__footer` on the page.
        const selectors = ['cart-drawer-items', '#CartDrawer .drawer__footer'];
        for (const selector of selectors) {
          const target = document.querySelector(selector);
          const source = html.querySelector(selector);
          if (target && source) target.replaceWith(source);
        }

        // The header bubble is NOT part of the cart-drawer section — fetching it out of
        // `html` above always returned null, so this was a silent no-op and the badge
        // kept whatever count the page was served with. It is its own section.
        //
        // Every matching node is updated, not just the first: the header renders TWO
        // elements carrying id="cart-icon-bubble" (duplicate ids, invalid but live), and
        // getElementById only ever reaches one of them.
        try {
          const bubbleRes = await fetch(`${cartUrl}?section_id=cart-icon-bubble`);
          if (bubbleRes.ok) {
            const bubbleDoc = new DOMParser().parseFromString(await bubbleRes.text(), 'text/html');
            const bubbleSource = bubbleDoc.querySelector('.shopify-section') || bubbleDoc.body;
            if (bubbleSource) {
              document.querySelectorAll('[id="cart-icon-bubble"]').forEach((node) => {
                node.innerHTML = bubbleSource.innerHTML;
              });
            }
          }
        } catch (bubbleError) {
          Utils.warn('Could not refresh the cart bubble:', bubbleError);
        }

        // Toggle is-empty on the root <cart-drawer> element so the theme
        // switches between the empty-state panel and the normal cart view.
        const liveCartDrawer = document.querySelector('cart-drawer');
        const sourceCartDrawer = html.querySelector('cart-drawer');
        if (liveCartDrawer && sourceCartDrawer) {
          liveCartDrawer.classList.toggle('is-empty', sourceCartDrawer.classList.contains('is-empty'));
        }

        // Sync the .drawer__inner-empty panel (the "Your cart is empty" block).
        // It lives inside .drawer__inner but outside the selectors above.
        const emptyTarget = document.querySelector('.drawer__inner-empty');
        const emptySource = html.querySelector('.drawer__inner-empty');
        if (emptySource && !emptyTarget) {
          // Cart just became empty — inject after top blocks (social proof / shipping / header).
          const insertAnchor =
            document.querySelector('.cart-update-social-proof') ||
            document.querySelector('.cart-update-shipping-zone') ||
            document.querySelector('.cart-legacy-shipping-zone') ||
            document.querySelector('.drawer__header');
          if (insertAnchor) insertAnchor.insertAdjacentElement('afterend', emptySource);
        } else if (emptySource && emptyTarget) {
          emptyTarget.replaceWith(emptySource);
        } else if (!emptySource && emptyTarget) {
          // Cart is no longer empty — remove the empty-state panel.
          emptyTarget.remove();
        }
      } catch (e) {
        Utils.warn('DiscountCodeWidget: cart drawer refresh failed:', e);
      }

      // Re-show the widget and restore applied state now that DOM is updated.
      this._showWidget();
      await this._syncWithCartState();
    },

    _showMsg(text, type) {
      // Held in state as well as the DOM: the drawer replaces its markup on every
      // cart update, which would otherwise wipe the message mid-flash.
      this._msgText = text || '';
      this._msgType = type || '';
      this._paintMsg();
    },

    _paintMsg() {
      const el = document.getElementById('gift-discount-code-message');
      if (!el) return;

      // A transient message (an error, or the apply confirmation) owns the line
      // while it lasts; otherwise it rests on the codes currently applied.
      const summary = this._appliedSummary();
      const text = this._msgText || summary;
      const type = this._msgText ? this._msgType : (summary ? 'success' : '');

      el.textContent = text;
      el.className = `gift-discount-code-widget__message${type ? ` gift-discount-code-widget__message--${type}` : ''}`;
    },


    _injectStyles() {
      if (document.getElementById('gift-discount-widget-styles')) return;
      const style = document.createElement('style');
      style.id = 'gift-discount-widget-styles';
      style.textContent = `
        .gift-discount-code-widget {
          padding: 0 0 14px;
        }
        .gift-discount-code-widget__label {
          display: block;
          font-size: 12px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          opacity: 0.65;
          margin-bottom: 7px;
        }
        .gift-discount-code-widget__inner {
          display: flex;
          gap: 8px;
          align-items: stretch;
        }
        .gift-discount-code-widget__input {
          flex: 1;
          min-width: 0;
          border: 1px solid rgba(var(--color-foreground), 0.25);
          border-radius: 4px;
          padding: 0 12px;
          height: 44px;
          font-size: 16px;
          background: transparent;
          color: rgb(var(--color-foreground));
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .gift-discount-code-widget__input::placeholder {
          text-transform: none;
          opacity: 0.45;
          letter-spacing: 0;
        }
        .gift-discount-code-widget__input:focus {
          outline: 2px solid rgb(var(--color-foreground));
          outline-offset: -1px;
          border-color: transparent;
        }
        .gift-discount-code-widget__input:disabled {
          opacity: 0.55;
        }
        /* Figma 5789:758 — solid brand orange at rest, lighter on hover, tinted
           and outlined when disabled. The orange and the Apercu stack are literals
           because this theme has no custom properties for either. */
        .gift-discount-code-widget__btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 100px;
          height: 44px;
          padding: 8px 15px;
          background: #e26201;
          /* Transparent at rest so the disabled state's border adds no size. */
          border: 1px solid transparent;
          border-radius: 4px;
          color: #fff;
          font-family: "Apercu Mono Pro", monospace;
          font-size: 16px;
          font-weight: 400;
          line-height: 16px;
          text-align: center;
          text-transform: uppercase;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
          transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
        }
        .gift-discount-code-widget__btn:hover:not(:disabled) {
          background: #df8b5a;
        }
        .gift-discount-code-widget__btn:disabled {
          background: #faeee8;
          border-color: #e26201;
          color: #e26201;
          cursor: default;
        }
        .gift-discount-code-widget__message {
          font-size: 12px;
          margin-top: 6px;
          min-height: 16px;
          line-height: 1.4;
        }
        .gift-discount-code-widget__message--error   { color: #c9372c; }
        .gift-discount-code-widget__message--success { color: #287d3c; }
      `;
      document.head.appendChild(style);
    }
  };

  let giftManagerInstance;

  function initializeGiftManager() {
    // Ahead of the manager and independent of it: the guard's listeners must be in
    // place from the first paint, and tryInit() below can spend two seconds waiting
    // for GIFT_RULES. A cart restored from a previous session is already clickable
    // during those two seconds.
    CheckoutGuard.init();

    if (giftManagerInstance) return;

    const maxRetries = 10;
    let retries = 0;
    
    function createManagerSafely() {
      try {
        giftManagerInstance = new GiftManager();
        DiscountCodeWidget.init(window.GIFT_RULES);
        createGlobalAPI();
      } catch (error) {
        Utils.error('Failed to initialize Gift Manager:', error);
      }
    }

    function tryInit() {
      const giftRules = window.GIFT_RULES;
      const hasValidRules = isNonEmptyRulesObject(giftRules);

      if (hasValidRules) {
        createManagerSafely();
      } else if (retries < maxRetries) {
        retries++;
        setTimeout(tryInit, 200);
      } else {
        Utils.warn('GIFT_RULES not available after max retries, initializing anyway');
        createManagerSafely();
      }
    }
    
    function createGlobalAPI() {
      window.giftManager = {
        instance: () => giftManagerInstance,
        
        addProductWithGifts: async (productData, giftVariantIds) => {
          return giftManagerInstance?.addProductWithGifts(productData, giftVariantIds) ?? null;
        },
        
        getApplicableGifts: async (productHandle, purchaseType) => {
          return giftManagerInstance?.getApplicableGifts(productHandle, purchaseType) ?? [];
        },
        
        checkAndApplyGifts: async () => {
          return giftManagerInstance?.checkAndApplyGifts();
        },
        
        removeItemWithConnectedGifts: async (itemToRemove, cartItems) => {
          return giftManagerInstance?.removeItemWithConnectedGifts(itemToRemove, cartItems) ?? null;
        },
        
        getGiftToTriggerMapping: () => {
          return giftManagerInstance?.giftToTriggerMapping ?? new Map();
        }
      };
    }
    
    tryInit();
  }

  function setupInitialization() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeGiftManager);
    } else {
      setTimeout(initializeGiftManager, 100);
    }
    
    if (!window.GIFT_RULES) {
      const checkInterval = setInterval(() => {
        const giftRules = window.GIFT_RULES;
        if (isNonEmptyRulesObject(giftRules)) {
          clearInterval(checkInterval);
          initializeGiftManager();
        }
      }, 500);
      
      setTimeout(() => clearInterval(checkInterval), CONFIG.TIMEOUTS.GIFT_RULES_CHECK);
    }
    
    let lastGiftRulesCheck = null;
    const watchForGiftRules = setInterval(() => {
      const giftRules = window.GIFT_RULES;
      if (!isNonEmptyRulesObject(giftRules)) return;

      // Skip the expensive JSON.stringify when the manager is already running with
      // rules loaded — avoids a 1s recurring task that can coincide with interactions
      if (giftManagerInstance?.initialized && giftManagerInstance?.evaluator) return;

      const currentRules = JSON.stringify(giftRules);
      if (currentRules !== lastGiftRulesCheck) {
        lastGiftRulesCheck = currentRules;
        if (giftManagerInstance) {
          giftManagerInstance.loadGiftRules();
        } else {
          initializeGiftManager();
        }
      }
    }, 1000);
    
    setTimeout(() => clearInterval(watchForGiftRules), CONFIG.TIMEOUTS.GIFT_RULES_WATCH);
  }

  window.triggerGiftCheck = async function() {
    if (window.giftManager?.instance()) {
      await window.giftManager.instance().checkAndApplyGifts();
    }
  };

  const AtcButtonHandler = {
    SELECTORS: [
      'button[data-product-id]',
      'a[href*="/cart/add"]'
    ],

    extractProductHandle(button) {
      const methods = [
        () => this.fromProductId(button),
        () => this.fromForm(button),
        () => this.fromParentContainer(button),
        () => this.fromLinkHref(button),
        () => this.fromProductCard(button)
      ];

      for (const method of methods) {
        const handle = method();
        if (handle) return handle;
      }

      return null;
    },

    fromProductId(button) {
      const productId = button.getAttribute('data-product-id');
      
      if (!productId) return null;
      
      const productElement = document.querySelector(
        `[data-product-id="${productId}"]`
      );
      
      if (productElement) {
        return productElement.getAttribute('data-product-handle') ||
               productElement.closest('[data-product-handle]')?.getAttribute('data-product-handle');
      }
      
      return null;
    },

    fromForm(button) {
      const form = button.closest('form');
      if (!form) return null;
      
      return form.getAttribute('data-product-handle') ||
             form.querySelector('[name="product-handle"]')?.value ||
             form.querySelector('[data-product-handle]')?.getAttribute('data-product-handle');
    },

    fromParentContainer(button) {
      const productContainer = button.closest('[data-product-handle], [data-product-url]');
      if (productContainer) {
        const handle = productContainer.getAttribute('data-product-handle');
        if (handle) return handle;
        
        const url = productContainer.getAttribute('data-product-url');
        if (url) return Utils.getProductHandleFromUrl(url);
      }
      
      return null;
    },

    fromLinkHref(button) {
      if (button.tagName !== 'A') return null;
      
      const href = button.getAttribute('href');
      if (href?.includes('/products/')) {
        return Utils.getProductHandleFromUrl(href);
      }
      
      return null;
    },

    fromProductCard(button) {
      const productCard = button.closest('[class*="product"]');
      if (productCard) {
        const handle = productCard.querySelector('[data-product-handle]')?.getAttribute('data-product-handle') ||
                      productCard.getAttribute('data-product-handle');
        
        if (handle) return handle;
        
        const productLink = productCard.querySelector('a[href*="/products/"]');
        if (productLink) {
          return Utils.getProductHandleFromUrl(productLink.href);
        }
      }
      
      return null;
    },

    addGiftTriggerToButtons() {
      this.SELECTORS.forEach(selector => {
        try {
          document.querySelectorAll(selector).forEach(button => {
            if (button.hasAttribute('data-gift-trigger')) return;
            const productHandle = this.extractProductHandle(button);
            
            if (productHandle) {
              button.setAttribute('data-gift-trigger', productHandle);
            }
          });
        } catch (error) {
          Utils.warn('Error processing add-to-cart buttons with selector:', selector, error);
        }
      });
    },

    setupMutationObserver() {
      if (!window.MutationObserver) return;

      // Guard flag: prevents multiple timeouts stacking when DOM changes arrive in bursts.
      // Without this, rapid mutations (e.g. cart drawer opening) would schedule
      // addGiftTriggerToButtons() once per batch rather than once total.
      let processingPending = false;

      const observer = new MutationObserver((mutations) => {
        if (processingPending) return;

        const shouldProcess = mutations.some(mutation =>
          Array.from(mutation.addedNodes).some(node =>
            node.nodeType === 1 && (
              node.querySelector?.('button[data-product-id], a[href*="/cart/add"]') ||
              node.matches?.('button[data-product-id], a[href*="/cart/add"]')
            )
          )
        );

        if (shouldProcess) {
          processingPending = true;
          setTimeout(() => {
            processingPending = false;
            this.addGiftTriggerToButtons();
          }, 100);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      setTimeout(() => observer.disconnect(), CONFIG.TIMEOUTS.ATC_BUTTON_OBSERVER);
    },

    setupPeriodicCheck() {
      let checkCount = 0;
      const maxChecks = 10;
      
      const checkInterval = setInterval(() => {
        checkCount++;
        this.addGiftTriggerToButtons();
        if (checkCount >= maxChecks) {
          clearInterval(checkInterval);
        }
      }, CONFIG.DELAYS.POST_CART_ADD_COOLDOWN);
    },

    init() {
      this.addGiftTriggerToButtons();
      this.setupMutationObserver();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          setTimeout(() => this.addGiftTriggerToButtons(), 500);
        });
      }
      window.addEventListener('load', () => {
        setTimeout(() => this.addGiftTriggerToButtons(), 1000);
      });
      this.setupPeriodicCheck();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      AtcButtonHandler.init();
    });
  } else {
    setTimeout(() => AtcButtonHandler.init(), 100);
  }

  setupInitialization();

  window.GiftExtensionConfig = CONFIG;
  window.GiftExtensionUtils = Utils;
  window.GiftDiscountWidget = DiscountCodeWidget;

})();
